import React, {
  useCallback,
  useState,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Table,
  Alert,
  Spinner,
  OverlayTrigger,
  Tooltip,
  Badge,
} from "react-bootstrap";
import AppToast from "../Toasts/AppToast";
import { useToast } from "../Toasts/useToast";
import {
  allocateReceipt,
  getInvoicesByAccount,
  getBankingDetailsByPracticeNumbers,
  sendOzowLink,
  createPaymentLink,
  getReceiptByNumber,
  createWebposPayment,
  fetchWebposPayment,
  sendInvoiceByEmail,
  getStatementPDF,
  getStatementToExport,
  sendStatementByEmail,
} from "../../afinanceapi";
import { getAccountByPatientId, getPracticeById, getPracticesByNumbers } from "../../api";
import SearchAccounts from "../SearchAccounts/SearchAccounts";
import { FaCheck, FaTimes } from "react-icons/fa";
import ozow from "../../Resources/ozowlogo.png";
import yoco from "../../Resources/yoco_logo.png";
import { ExportPDFReceipt } from "./ExportReceiptPDF";
import { InputGroup } from "react-bootstrap";
import { FaPlus, FaTrash } from "react-icons/fa";

import "./CreateInvoice.css";
import "./acc-list.css";

// Canonical labels for patient-side methods
const PAYMENT_METHOD_LABELS = {
  cash: "Cash",
  creditCard: "Credit Card",
  eft: "EFT",
  paymentLink: "Payment Link",
};

export default function CreateReceiptModal({
  show,
  onHide = () => {},
  account,
  patientId,
  invoiceNumber,
}) {
  // Core state
  const [accounts, setAccounts] = useState([]);
  const [invoiceList, setInvoiceList] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [accountSelected, setAccountSelected] = useState(null);
  const [allocations, setAllocations] = useState({});
  const [outstandingBalances, setOutstandingBalances] = useState({});
  const [selectedPaymentSources, setSelectedPaymentSources] = useState({});
  const [selectedInvoiceAmounts, setSelectedInvoiceAmounts] = useState({});

  // Payment state
  const [paymentMethod, setPaymentMethod] = useState();
  const [paidBy, setPaidBy] = useState();
  const [referenceNumber, setReferenceNumber] = useState("");
  const [receivedAmount, setReceivedAmount] = useState("");
  const [changeAmount, setChangeAmount] = useState(0);
  const [addUnallocated, setAddUnallocated] = useState(true);
  const [useUnallocated, setUseUnallocated] = useState(false);
  const [unallocatedTendered, setUnallocatedTendered] = useState("");
  const [closingUnallocated, setClosingUnallocated] = useState(0);
  const [customAllocPatient, setCustomAllocPatient] = useState("");
  const [customAllocMedAid, setCustomAllocMedAid] = useState("");
  const [customAllocSource, setCustomAllocSource] = useState(null);
  const [receiptDate, setReceiptDate] = useState(
    () => new Date().toISOString().split("T")[0]
  );

  // Per-line payment selection (expandable invoice rows)
  const [expandedInvoices, setExpandedInvoices] = useState(() => new Set());
  // key `${invoiceId}_${lineNumber}` → { invoiceId, lineNumber, source,
  //   amountText ("R 12.34" input value), maxAmount (line outstanding, capped
  //   at the invoice-level outstanding remaining for that source) }
  const [selectedLineCells, setSelectedLineCells] = useState({});

  // UI state
  const [closeAlert, setCloseAlert] = useState(false);
  const { toast, showToast, hideToast } = useToast();
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [isAllocating, setIsAllocating] = useState(false);
  const [showPostReceiptModal, setShowPostReceiptModal] = useState(false);
  const [showEmailFields, setShowEmailFields] = useState(false);
  const [emailSending, setEmailSending] = useState(false);

  // Practice & Banking
  const [selectedPractice, setSelectedPractice] = useState("");
  const [bankingDetails, setBankingDetails] = useState({});
  const [fullPractice, setFullPractice] = useState(null);
  // Fallback practice list derived from the selected account itself. Used so
  // the unallocated-funds custom-allocation row stays usable when the account
  // has no open invoices (otherwise practiceOptions would be empty and the
  // entire allocation table — including the custom row — would be hidden).
  const [accountPracticeOptions, setAccountPracticeOptions] = useState([]);

  // Email & Receipt
  const [email, setEmail] = useState();
  const [emailToSend, setEmailToSend] = useState("");
  const [emailLinkSent, setEmailLink] = useState(false);
  const [paymentLinkReference, setPaymentLinkReference] = useState("");
  const [createdReceipt, setCreatedReceipt] = useState(null);
  const [receiptBlob, setReceiptBlob] = useState(null);
  const [receiptEmailData, setReceiptEmailData] = useState(null);
  const [receiptId, setReceiptId] = useState(null);
  const [includeReceipt, setIncludeReceipt] = useState(true);
  const [includeStatement, setIncludeStatement] = useState(false);

  // Yoco state
  const [yocoDevices, setYocoDevices] = useState([]);
  const [selectedYocoDevice, setSelectedYocoDevice] = useState("");
  const [yocoRedirectUrl, setYocoRedirectUrl] = useState("");
  const [yocoPaymentId, setYocoPaymentId] = useState("");
  const [showYocoIframe, setShowYocoIframe] = useState(false);
  const [yocoBusy, setYocoBusy] = useState(false);
  const [yocoError, setYocoError] = useState("");
  const [yocoPaymentStatus, setYocoPaymentStatus] = useState("");

  const EXTRA_EMAIL_FIELDS = ["email2", "email3", "email4", "email5"];

// Receipt email UX state (for the confirmation modal)
const [receiptEmailPrimary, setReceiptEmailPrimary] = useState("");
const [receiptExtraEmails, setReceiptExtraEmails] = useState({
  email2: undefined,
  email3: undefined,
  email4: undefined,
  email5: undefined,
});


  const navigate = useNavigate();
  const allocateRef = useRef();

  const allReceiptExtrasUsed = EXTRA_EMAIL_FIELDS.every(
  (f) => receiptExtraEmails[f] !== undefined
);

const addReceiptEmailField = () => {
  const next = EXTRA_EMAIL_FIELDS.find((f) => receiptExtraEmails[f] === undefined);
  if (!next) return;
  setReceiptExtraEmails((prev) => ({ ...prev, [next]: "" }));
};

const removeReceiptEmailField = (field) => {
  setReceiptExtraEmails((prev) => ({ ...prev, [field]: undefined }));
};

const changeReceiptExtra = (field, value) => {
  setReceiptExtraEmails((prev) => ({ ...prev, [field]: value }));
};



  // Derived values
  // practiceOptions = invoices' practices ∪ account's practices. The union
  // means the practice dropdown (and the allocation table) stays usable when
  // the account has open invoices, no invoices at all, or a mix — so the
  // custom-allocation row for unallocated funds is always reachable.
  const practiceOptions = useMemo(
    () => {
      const byNumber = new Map();
      for (const inv of invoiceList) {
        const p = inv?.practice;
        if (p?.practiceName && p?.practiceNumber) {
          byNumber.set(p.practiceNumber, p);
        }
      }
      for (const p of accountPracticeOptions) {
        if (p?.practiceNumber && !byNumber.has(p.practiceNumber)) {
          byNumber.set(p.practiceNumber, p);
        }
      }
      return Array.from(byNumber.values());
    },
    [invoiceList, accountPracticeOptions]
  );

  const openingBalance = useMemo(
    () =>
      Number(
        accountSelected?.unallocated?.find(
          (u) => u.practiceNumber === selectedPractice
        )?.practiceBalance ?? 0
      ),
    [accountSelected, selectedPractice]
  );

  // Money parser shared by the per-line selection logic ("R 1 234.56" → 1234.56)
  const parseMoneyValue = (v) =>
    typeof v === "number"
      ? v
      : parseFloat(String(v || "").replace(/[^0-9.]/g, "")) || 0;

  // Outstanding portion of a single invoice line for a payer side. Lines carry
  // cumulative per-line paid tracking (patientPaid / medAidPaid) once they have
  // been receipted per-line; older lines simply have no paid fields yet.
  const lineOutstanding = (line, source) => {
    const total =
      source === "patient" ? line?.patientAmount || 0 : line?.medAidAmount || 0;
    const paid =
      source === "patient" ? line?.patientPaid || 0 : line?.medAidPaid || 0;
    return Math.max(0, parseFloat((total - paid).toFixed(2)));
  };

  // Per-line allocation is only offered while the line-level paid tracking
  // (patientPaid / medAidPaid) accounts for every payment already on the
  // invoice. An invoice-level receipt updates outstandingBalance without
  // stamping the lines, so the lines would claim more owed than the invoice
  // does — allocating per line on top would double-count the earlier
  // payment. The allocate endpoint enforces the same rule server-side.
  const canPayPerLine = (inv) => {
    const lineTrackedPaid = (inv?.lines || []).reduce(
      (s, l) =>
        s + (parseFloat(l.patientPaid) || 0) + (parseFloat(l.medAidPaid) || 0),
      0
    );
    const invoicePaid = parseFloat(inv?.outstandingBalance?.paidAmount) || 0;
    return Math.abs(lineTrackedPaid - invoicePaid) <= 0.02;
  };

  const PER_LINE_BLOCKED_MSG =
    "Earlier payments on this invoice were not allocated line by line — allocate this payment at invoice level.";

  // Line selections grouped per invoice — { [invoiceId]: [selection, ...] }
  const lineSelectionsByInvoice = useMemo(() => {
    const map = {};
    Object.values(selectedLineCells).forEach((sel) => {
      if (!map[sel.invoiceId]) map[sel.invoiceId] = [];
      map[sel.invoiceId].push(sel);
    });
    return map;
  }, [selectedLineCells]);

  const lineSelectionCount = Object.keys(selectedLineCells).length;

  const lineSelectedTotal = useMemo(
    () =>
      Object.values(selectedLineCells).reduce(
        (s, sel) => s + parseMoneyValue(sel.amountText),
        0
      ),
    [selectedLineCells]
  );

  // Every invoice involved in this receipt (whole-invoice + per-line)
  const allSelectedInvoiceIds = useMemo(
    () => [
      ...new Set([...selectedInvoices, ...Object.keys(lineSelectionsByInvoice)]),
    ],
    [selectedInvoices, lineSelectionsByInvoice]
  );

  const totalSelectedAmount = useMemo(() => {
    // Custom allocation takes precedence (mutually exclusive with invoice selection)
    if (
      customAllocSource &&
      selectedInvoices.length === 0 &&
      lineSelectionCount === 0
    ) {
      return customAllocSource === "patient"
        ? parseMoneyValue(customAllocPatient)
        : parseMoneyValue(customAllocMedAid);
    }

    const invoicePart = selectedInvoices.reduce((sum, invoiceId) => {
      const paymentSource = selectedPaymentSources[invoiceId];
      if (!paymentSource) return sum;

      const balance = outstandingBalances[invoiceId];
      if (!balance) return sum;

      const amount =
        paymentSource === "patient"
          ? balance.patientOutstanding || 0
          : balance.medAidOutstanding || 0;

      return sum + amount;
    }, 0);

    return parseFloat((invoicePart + lineSelectedTotal).toFixed(2));
  }, [selectedInvoices, selectedPaymentSources, outstandingBalances, customAllocSource, customAllocPatient, customAllocMedAid, lineSelectionCount, lineSelectedTotal]);

  const totalOutstandingAfter = useMemo(() => {
    const sumAllocated = Object.values(allocations).reduce(
      (s, a) => s + (a.receivedAmount || 0),
      0
    );
    return Math.max(0, parseFloat((totalSelectedAmount - sumAllocated).toFixed(2)));
  }, [allocations, totalSelectedAmount]);

  const tenderedLabel = useMemo(() => {
    const labels = { cash: "Cash", creditCard: "Card", eft: "EFT", paymentLink: "Link" };
    return paymentMethod ? `${labels[paymentMethod] || "Amount"} Tendered` : "Amount Tendered";
  }, [paymentMethod]);

  // True when the user is paying ONLY from unallocated (no payment method).
  // When true: the Tendered input is locked (receivedAmount must be 0) and
  // the unallocated cap is the invoice total — they can't draw more than
  // they're paying off.
  const onlyUnallocated = useUnallocated && !paymentMethod;

  const totalTendered = useMemo(() => {
    const parse = (v) =>
      typeof v === "number"
        ? v
        : parseFloat(String(v || "").replace(/[^0-9.]/g, "")) || 0;
    const cash = parse(receivedAmount);
    const unalloc = useUnallocated
      ? Math.min(parse(unallocatedTendered), openingBalance, totalSelectedAmount)
      : 0;
    return cash + unalloc;
  }, [receivedAmount, useUnallocated, unallocatedTendered, openingBalance, totalSelectedAmount]);

  // Utility functions
  const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return "0.00"; // fallback

  return num.toFixed(2); // ALWAYS uses "." regardless of locale
};

  const resetModalState = useCallback(() => {
    setSelectedInvoices([]);
    setInvoiceList([]);
    setReceivedAmount(0);
    setReferenceNumber("");
    setSelectedPaymentSources({});
    setOutstandingBalances({});
    setSelectedLineCells({});
    setExpandedInvoices(new Set());
    setPaymentMethod(undefined);
    setPaidBy(undefined);
    setUseUnallocated(false);
    setUnallocatedTendered("");
    setClosingUnallocated(0);
    setCustomAllocPatient("");
    setCustomAllocMedAid("");
    setCustomAllocSource(null);
    setErrorMessage("");
    setIncludeReceipt(true);
    setIncludeStatement(false);
    hideToast();
  }, []);

  const buildReceiptEmailData = useCallback(
    ({ receipt, account, practice }) => {
      const practiceNumber = practice?.practiceNumber || "";
      const patientFirstName = account?.accountFullNames || "";
      const patientLastName = account?.accountLastName || "";

      return {
        practiceNumbers: practiceNumber ? [String(practiceNumber)] : [],
        practiceName: practice?.practiceName || "",
        practiceEmail: practice?.practiceEmail || "",
        practicePhone: practice?.practicePhoneNumber || "",
        practiceNumber,
        logoURL: practice?.logoURL || "",
        patientFirstName,
        patientLastName,
        patientName: [patientFirstName, patientLastName]
          .filter(Boolean)
          .join(" "),
        receiptNumber: receipt?.receiptNumber || "",
        receiptDate: receipt?.receiptDate || "",
      };
    },
    []
  );

  // Earliest invoice date for the loaded account, used as the statement
  // period start when emailing/downloading the statement alongside a receipt.
  // Replaces the previous hardcoded "1900-01-01" so the PDF's opening-balance
  // date and the email body show a sensible period. Falls back to today when
  // the account has no invoices yet.
  const computeStatementStartDate = useCallback(() => {
    const dates = (invoiceList || [])
      .map((inv) => inv?.invoiceDate)
      .filter(Boolean)
      .map((d) => new Date(d))
      .filter((d) => !Number.isNaN(d.getTime()))
      .sort((a, b) => a - b);
    if (dates.length === 0) return new Date().toISOString().split("T")[0];
    return dates[0].toISOString().split("T")[0];
  }, [invoiceList]);

  // Effects
  useEffect(() => {
    setAccountSelected(account || null);
    setEmail(account?.accountEmail || "");
  }, [account]);

  useEffect(() => {
    if (!show) resetModalState();
  }, [show, resetModalState]);

  useEffect(() => {
    const fetchAccount = async () => {
      if (account) {
        setAccounts([account]);
        return;
      }

      if (!patientId) return;

      try {
        const data = await getAccountByPatientId(patientId);
        setAccounts(data);
        setAccountSelected(data);
      } catch (error) {
        console.error("Error fetching account:", error);
        setAccounts([]);
      }
    };

    fetchAccount();
  }, [account, patientId]);

  // Resolve the selected account's practice numbers to full practice objects
  // (so the dropdown can show real names even when there are no invoices to
  // borrow them from). Caller-side JWT scope already narrows the response.
  useEffect(() => {
    let cancelled = false;
    const raw = accountSelected?.accountPractice;
    const nums = Array.isArray(raw) ? raw : (raw ? [raw] : []);
    if (nums.length === 0) {
      setAccountPracticeOptions([]);
      return;
    }
    getPracticesByNumbers(nums)
      .then((data) => {
        if (cancelled) return;
        setAccountPracticeOptions(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setAccountPracticeOptions([]);
      });
    return () => { cancelled = true; };
  }, [accountSelected]);

  useEffect(() => {
    if (practiceOptions?.length > 0 && !selectedPractice) {
      setSelectedPractice(practiceOptions[0]?.practiceNumber);
    }
  }, [practiceOptions, selectedPractice]);

  useEffect(() => {
    const fetchInvoices = async () => {
      if (!accountSelected) return;

      try {
        const data = await getInvoicesByAccount(accountSelected._id);

        const balances = {};
        data.forEach((invoice) => {
          balances[invoice._id] = {
            patientOutstanding:
              invoice.outstandingBalance?.patientOutstanding || 0,
            medAidOutstanding:
              invoice.outstandingBalance?.medAidOutstanding || 0,
            totalOutstanding: invoice.outstandingBalance?.totalOutstanding || 0,
            paidAmount: invoice.outstandingBalance?.paidAmount || 0,
            originalAmount: invoice.totalAmount || 0,
          };
        });

        setOutstandingBalances(balances);
        setInvoiceList(data);

        if (invoiceNumber) {
          const match = data.find((inv) => inv.invoiceNumber === invoiceNumber);
          if (match) {
            setSelectedInvoices([match._id]);
          }
        }
      } catch (error) {
        console.error("Error fetching invoices:", error);
        setInvoiceList([]);
        setOutstandingBalances({});
      }
    };

    fetchInvoices();
  }, [accountSelected, invoiceNumber]);

  useEffect(() => {
    const fetchBankingDetails = async () => {
      if (!selectedPractice) return;

      try {
        const [bankingResult, practiceData] = await Promise.all([
          getBankingDetailsByPracticeNumbers([selectedPractice]),
          getPracticeById(selectedPractice),
        ]);

        setBankingDetails(bankingResult[0]);
        setFullPractice(practiceData);
        setYocoDevices(practiceData.integrations?.yocoWebPOS?.devices || []);
        setSelectedYocoDevice(
          practiceData.integrations?.yocoWebPOS?.devices[0]?.id || ""
        );
      } catch (error) {
        console.error("Error fetching banking details:", error);
      }
    };

    fetchBankingDetails();
  }, [selectedPractice]);

  // Payment amount calculation (per-line sources + sorted allocation)
  useEffect(() => {
    // ---- helpers -------------------------------------------------------------
    const parseMoney = (v) =>
      typeof v === "number"
        ? v
        : parseFloat(String(v || "").replace(/[^0-9.]/g, "")) || 0;

    // Decide who pays per invoice id
    const paymentSourceFor = (id) => {
      // explicit user choice first
      if (selectedPaymentSources[id]) return selectedPaymentSources[id];
      // global "Paid by" if explicitly set
      if (paidBy) return paidBy;
      // fallback: if invoice still has MA outstanding, treat as medicalAid; else patient
      const bal = outstandingBalances[id] || {};
      return (bal.medAidOutstanding || 0) > 0 ? "medicalAid" : "patient";
    };

    // ---- compute the remaining pot we can allocate --------------------------
    const parsedReceived = parseMoney(receivedAmount);
    const parsedUnallocated = useUnallocated
      ? Math.min(parseMoney(unallocatedTendered), openingBalance, totalSelectedAmount)
      : 0;

    // medical aid can never draw from unallocated, only add to it;
    // patient must explicitly opt in via useUnallocated button
    let remaining =
      paidBy === "medicalAid"
        ? parsedReceived
        : parsedReceived + parsedUnallocated;

    // Line-mode invoices: selected specific lines instead of the whole side.
    const lineSelectionMap = {};
    Object.values(selectedLineCells).forEach((sel) => {
      if (!lineSelectionMap[sel.invoiceId]) lineSelectionMap[sel.invoiceId] = [];
      lineSelectionMap[sel.invoiceId].push(sel);
    });
    const lineInvoiceIds = Object.keys(lineSelectionMap);

    // nothing selected? reset state and bail early
    if (
      (!selectedInvoices?.length && lineInvoiceIds.length === 0) ||
      !invoiceList?.length
    ) {
      setAllocations({});
      setClosingUnallocated(
        parseFloat((openingBalance - parsedUnallocated + remaining).toFixed(2))
      );
      setChangeAmount(0);
      return;
    }

    // ---- build sorted target list (whole-invoice + line-mode) ---------------
    const parseSel = (v) =>
      parseFloat(String(v || "").replace(/[^0-9.]/g, "")) || 0;
    const targets = [
      ...selectedInvoices.map((id) => ({ id, mode: "invoice" })),
      ...lineInvoiceIds
        .filter((id) => !selectedInvoices.includes(id))
        .map((id) => ({ id, mode: "lines" })),
    ]
      .map(({ id, mode }) => ({
        invoice: invoiceList.find((inv) => inv._id === id),
        id,
        mode,
        paymentSource:
          mode === "lines"
            ? lineSelectionMap[id][0].source
            : paymentSourceFor(id),
      }))
      .filter((item) => item.invoice)
      .sort(
        (a, b) =>
          new Date(a.invoice.invoiceDate) - new Date(b.invoice.invoiceDate)
      );

    // ---- walk the sorted list, allocate from "remaining" ---------------------
    const nextAllocations = {};

    for (const { invoice, id, mode, paymentSource } of targets) {
      // ignore invoices that cannot be allocated against
      if (["Pending", "Paid"].includes(invoice.status)) continue;

      const bal = outstandingBalances[id] || {
        patientOutstanding: 0,
        medAidOutstanding: 0,
        totalOutstanding: 0,
        paidAmount: 0,
      };

      const due =
        mode === "lines"
          ? lineSelectionMap[id].reduce(
              (s, sel) => s + parseSel(sel.amountText),
              0
            )
          : paymentSource === "patient"
          ? bal.patientOutstanding || 0
          : bal.medAidOutstanding || 0;

      const alloc = Math.min(due, Math.max(0, remaining));
      if (alloc <= 0) continue;

      nextAllocations[id] = {
        receivedAmount: parseFloat(alloc.toFixed(2)),
        paymentSource,
      };

      remaining -= alloc;
      if (remaining <= 0) break;
    }

    // ---- push to state + handle unallocated/change ---------------------------
    setAllocations(nextAllocations);

    const unallocatedDrawn = parsedUnallocated;
    const isGiveChange = paymentMethod === "cash" && !addUnallocated;

    if (isGiveChange && remaining > 0) {
      // "Give change" for cash: return leftover as change
      setChangeAmount(remaining);
      setClosingUnallocated(openingBalance - unallocatedDrawn);
    } else {
      setChangeAmount(0);
      // When cash "Add to Unallocated" with surplus, the full surplus
      // goes to unallocated without deducting the draw (it is returned
      // as part of the surplus). For all other cases, deduct normally.
      const deductDrawn =
        paymentMethod === "cash" && addUnallocated && remaining > 0
          ? 0
          : unallocatedDrawn;
      setClosingUnallocated(
        parseFloat((openingBalance - deductDrawn + remaining).toFixed(2))
      );
    }
  }, [
    receivedAmount,
    selectedInvoices,
    selectedLineCells,
    paidBy,
    invoiceList,
    selectedPaymentSources,
    outstandingBalances,
    addUnallocated,
    useUnallocated,
    unallocatedTendered,
    openingBalance,
    paymentMethod,
    setAllocations,
    setChangeAmount,
  ]);

  // Auto-set received amount for certain payment methods
  useEffect(() => {
    // When custom alloc is active, always auto-set tendered for ALL methods
    if (customAllocSource) {
      setReceivedAmount(`R ${totalSelectedAmount.toFixed(2)}`);
      return;
    }
    // Per-line picks are exact amounts, so pre-fill the tendered box with
    // their total for every method (cash can still be bumped up for change).
    const lineMode = lineSelectionCount > 0;
    if (!lineMode && !["creditCard", "eft"].includes(paymentMethod)) return;
    // When splitting with unallocated, don't force receivedAmount to total
    if (useUnallocated) return;

    setReceivedAmount(`R ${totalSelectedAmount.toFixed(2)}`);
  }, [paymentMethod, totalSelectedAmount, useUnallocated, customAllocSource, lineSelectionCount]);

  // Clear receivedAmount when paying ONLY with unallocated funds — the
  // Tendered input is disabled in that mode, but a stale value would otherwise
  // poison submit math (totalReceived = newMoney + unallocatedUsed).
  useEffect(() => {
    if (onlyUnallocated) setReceivedAmount(0);
  }, [onlyUnallocated]);

  // Auto-default unallocated tendered to difference between total and received
  useEffect(() => {
    if (!useUnallocated) {
      setUnallocatedTendered("");
      return;
    }
    const parseMoney = (v) =>
      typeof v === "number"
        ? v
        : parseFloat(String(v || "").replace(/[^0-9.]/g, "")) || 0;
    const received = parseMoney(receivedAmount);
    const diff = Math.max(0, totalSelectedAmount - received);
    const capped = Math.min(diff, openingBalance, totalSelectedAmount);
    setUnallocatedTendered(`R ${capped.toFixed(2)}`);
  }, [useUnallocated, totalSelectedAmount, receivedAmount, openingBalance]);

  // Yoco iframe message handler
  useEffect(() => {
    if (!showYocoIframe || !yocoRedirectUrl || !yocoPaymentId) return;

    const expectedOrigin = (() => {
      try {
        return new URL(yocoRedirectUrl).origin;
      } catch {
        return "";
      }
    })();

    const finishedRef = { current: false };

    const onMessage = async (ev) => {
      if (expectedOrigin && ev.origin !== expectedOrigin) return;
      const evt = ev?.data?.event;
      if (!evt || evt !== "payment_result_acknowledged" || finishedRef.current)
        return;

      finishedRef.current = true;
      try {
        const { status } = await fetchWebposPayment(
          selectedYocoDevice,
          yocoPaymentId,
          selectedPractice
        );
        setYocoPaymentStatus(status);

        const okStatuses = [
          "authorized",
          "captured",
          "successful",
          "approved",
          "completed",
        ];
        const ok = okStatuses.includes(String(status || "").toLowerCase());

        if (!ok) {
          setYocoError(
            `Payment not approved (status: ${status || "unknown"}).`
          );
        } else {
          setPaymentMethod("creditCard");
          setReferenceNumber((ref) => ref || `Yoco ${yocoPaymentId}`);
          await allocateRef.current();
        }
      } catch (e) {
        console.error(
          "Finalize Yoco payment error:",
          e.response?.data || e.message
        );
        setYocoError("Could not confirm payment result.");
      } finally {
        setShowYocoIframe(false);
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [
    showYocoIframe,
    yocoRedirectUrl,
    yocoPaymentId,
    selectedYocoDevice,
    selectedPractice,
  ]);

  // Event handlers
  const handleChange = (e) => {
    const { name, value } = e.target;

    let sanitized = value.replace(/[^0-9.,]/g, "");
    const trailingSep = /[.,]$/.test(sanitized);
    const sepIndex = sanitized.search(/[.,]/);

    let intPart, decPart;
    if (sepIndex >= 0) {
      intPart = sanitized.slice(0, sepIndex);
      decPart = sanitized.slice(sepIndex + 1);
    } else {
      intPart = sanitized;
      decPart = "";
    }

    intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    let formatted = intPart;

    if (sepIndex >= 0) {
      formatted += ".";
      if (!trailingSep) {
        formatted += decPart.slice(0, 2);
      }
    }

    const parseMoney = (v) =>
      parseFloat(String(v || "").replace(/[^0-9.]/g, "")) || 0;
    const enteredValue = parseMoney(formatted);
    const isSplitWithUnalloc = useUnallocated;

    if (name === "receivedAmount") {
      if (isSplitWithUnalloc) {
        // Hard cap only at the absolute limit
        const capped = Math.min(enteredValue, totalSelectedAmount);
        if (enteredValue > totalSelectedAmount) {
          setReceivedAmount(`R ${totalSelectedAmount.toFixed(2)}`);
        } else {
          setReceivedAmount(`R ${formatted}`);
        }
        // Auto-adjust unallocated to complement
        const newUnalloc = Math.min(
          Math.max(0, totalSelectedAmount - capped),
          openingBalance
        );
        setUnallocatedTendered(`R ${newUnalloc.toFixed(2)}`);
      } else {
        setReceivedAmount(`R ${formatted}`);
      }
    } else if (name === "unallocatedTendered") {
      if (isSplitWithUnalloc) {
        // Hard cap at openingBalance or totalSelectedAmount
        const maxUnalloc = Math.min(totalSelectedAmount, openingBalance);
        const capped = Math.min(enteredValue, maxUnalloc);
        if (enteredValue > maxUnalloc) {
          setUnallocatedTendered(`R ${maxUnalloc.toFixed(2)}`);
        } else {
          setUnallocatedTendered(`R ${formatted}`);
        }
        // Auto-adjust received to complement
        const newReceived = Math.max(0, totalSelectedAmount - capped);
        setReceivedAmount(`R ${newReceived.toFixed(2)}`);
      } else {
        setUnallocatedTendered(`R ${formatted}`);
      }
    } else if (name === "customAllocPatient") {
      setCustomAllocPatient(`R ${formatted}`);
    } else if (name === "customAllocMedAid") {
      setCustomAllocMedAid(`R ${formatted}`);
    }
  };

  const handleCustomAllocBlur = (source) => {
    const parse = (v) =>
      parseFloat(String(v || "").replace(/[^0-9.]/g, "")) || 0;
    const value =
      source === "patient"
        ? parse(customAllocPatient)
        : parse(customAllocMedAid);

    if (value > 0 && selectedInvoices.length === 0 && lineSelectionCount === 0) {
      setCustomAllocSource(source);
      setPaidBy(source);
      // Only one source per receipt — clear the other field
      if (source === "patient") setCustomAllocMedAid("");
      else setCustomAllocPatient("");
    } else if (value <= 0 && customAllocSource === source) {
      setCustomAllocSource(null);
      setPaidBy(undefined);
    }
  };

  const handleCellClick = (invoiceId, type, amount) => {
    // Block invoice selection when custom allocation row is active
    if (customAllocSource) return;

    const invoice = invoiceList.find((inv) => inv._id === invoiceId);
    if (!invoice || ["Pending", "Paid"].includes(invoice.status) || !amount) {
      return;
    }

    const effectivePaidBy = paidBy || type;

    if (!paidBy) {
      setPaidBy(type);
    }

    if (type !== effectivePaidBy) {
      return;
    }

    const alreadySelected =
      selectedInvoices.includes(invoiceId) &&
      selectedPaymentSources[invoiceId] === type;

    if (alreadySelected) {
      setSelectedInvoices((prev) => prev.filter((id) => id !== invoiceId));
      setSelectedPaymentSources((prev) => {
        const updated = { ...prev };
        delete updated[invoiceId];
        return updated;
      });
      setSelectedInvoiceAmounts((prev) => {
        const updated = { ...prev };
        delete updated[invoiceId];
        return updated;
      });

      if (selectedInvoices.length === 1 && lineSelectionCount === 0) {
        setPaidBy("");
      }
    } else {
      setSelectedInvoices((prev) =>
        prev.includes(invoiceId) ? prev : [...prev, invoiceId]
      );
      setSelectedPaymentSources((prev) => ({ ...prev, [invoiceId]: type }));
      setSelectedInvoiceAmounts((prev) => ({ ...prev, [invoiceId]: amount }));
      // Whole-invoice selection and per-line selection are mutually exclusive
      // for the same invoice — clear this invoice's line picks.
      setSelectedLineCells((prev) => {
        const next = {};
        Object.entries(prev).forEach(([key, sel]) => {
          if (sel.invoiceId !== invoiceId) next[key] = sel;
        });
        return next;
      });
    }
  };

  const toggleInvoiceExpand = (invoiceId) => {
    setExpandedInvoices((prev) => {
      const next = new Set(prev);
      if (next.has(invoiceId)) next.delete(invoiceId);
      else next.add(invoiceId);
      return next;
    });
  };

  // Select / deselect a specific line's Patient or Med Aid outstanding for
  // payment. Selecting pre-fills the full line outstanding; the amount can
  // then be edited down (a med-aid remainder moves to the patient on
  // allocation — the ERA principle).
  const toggleLineCell = (invoiceId, line, source) => {
    if (customAllocSource) return;

    const invoice = invoiceList.find((inv) => inv._id === invoiceId);
    if (!invoice || ["Pending", "Paid"].includes(invoice.status)) return;

    const key = `${invoiceId}_${line.lineNumber}`;
    const existing = selectedLineCells[key];

    // Deselect (same source clicked again / clear icon)
    if (existing && existing.source === source) {
      setSelectedLineCells((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      if (lineSelectionCount === 1 && selectedInvoices.length === 0) {
        setPaidBy("");
      }
      return;
    }

    const outstanding = lineOutstanding(line, source);
    if (outstanding <= 0) return;

    const effectivePaidBy = paidBy || source;
    if (source !== effectivePaidBy) return; // single payer per receipt

    // Cap the selectable amount at what the invoice still owes on this side —
    // older invoices receipted at invoice level keep their original line split,
    // so the line sum can exceed the true outstanding.
    const bal = outstandingBalances[invoiceId] || {};
    const invoiceOutstanding =
      source === "patient"
        ? bal.patientOutstanding || 0
        : bal.medAidOutstanding || 0;
    const otherSelected = Object.entries(selectedLineCells)
      .filter(([k, sel]) => k !== key && sel.invoiceId === invoiceId)
      .reduce((s, [, sel]) => s + parseMoneyValue(sel.amountText), 0);
    const cap = Math.max(
      0,
      parseFloat((invoiceOutstanding - otherSelected).toFixed(2))
    );
    if (cap <= 0) {
      setErrorMessage(
        "The selected lines already cover this invoice's outstanding amount."
      );
      return;
    }
    const maxAmount = Math.min(outstanding, cap);

    if (!paidBy) setPaidBy(source);

    setSelectedLineCells((prev) => ({
      ...prev,
      [key]: {
        invoiceId,
        lineNumber: line.lineNumber,
        source,
        amountText: `R ${maxAmount.toFixed(2)}`,
        maxAmount,
      },
    }));

    // Per-line selection replaces a whole-invoice selection on this invoice.
    setSelectedInvoices((prev) => prev.filter((id) => id !== invoiceId));
    setSelectedPaymentSources((prev) => {
      const updated = { ...prev };
      delete updated[invoiceId];
      return updated;
    });
    setSelectedInvoiceAmounts((prev) => {
      const updated = { ...prev };
      delete updated[invoiceId];
      return updated;
    });
  };

  // Custom amount typed into a selected line cell — same sanitising as the
  // other money inputs, hard-capped at the line's selectable outstanding.
  const handleLineAmountChange = (key, value) => {
    const sel = selectedLineCells[key];
    if (!sel) return;

    let sanitized = value.replace(/[^0-9.,]/g, "");
    const trailingSep = /[.,]$/.test(sanitized);
    const sepIndex = sanitized.search(/[.,]/);

    let intPart, decPart;
    if (sepIndex >= 0) {
      intPart = sanitized.slice(0, sepIndex);
      decPart = sanitized.slice(sepIndex + 1);
    } else {
      intPart = sanitized;
      decPart = "";
    }

    intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    let formatted = intPart;
    if (sepIndex >= 0) {
      formatted += ".";
      if (!trailingSep) formatted += decPart.slice(0, 2);
    }

    const entered = parseMoneyValue(formatted);
    const nextText =
      entered > sel.maxAmount
        ? `R ${sel.maxAmount.toFixed(2)}`
        : `R ${formatted}`;

    setSelectedLineCells((prev) => ({
      ...prev,
      [key]: { ...prev[key], amountText: nextText },
    }));
  };

  // Empty / zero amount on blur deselects the line.
  const handleLineAmountBlur = (key) => {
    const sel = selectedLineCells[key];
    if (!sel) return;
    if (parseMoneyValue(sel.amountText) <= 0) {
      setSelectedLineCells((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      if (lineSelectionCount === 1 && selectedInvoices.length === 0) {
        setPaidBy("");
      }
    }
  };

  const handlePaymentMethod = (e) => {
    setPaymentMethod(e.target.name);
  };

  const handleAllocate = useCallback(async () => {
    if (isAllocating) return;
    // --- quick helpers ---
    const parseMoney = (v) =>
      typeof v === "number"
        ? v
        : parseFloat(String(v || "").replace(/[^0-9.]/g, "")) || 0;

    const allSame = (arr) => arr.every((v) => v === arr[0]);

    // --- validations / pick the single payer for this receipt ---
    // Collect explicit choices — whole-invoice column clicks AND per-line picks
    const explicitSources = [
      ...selectedInvoices
        .map((id) => selectedPaymentSources[id])
        .filter(Boolean),
      ...Object.values(selectedLineCells).map((sel) => sel.source),
    ]; // ["patient","medicalAid",...]

    // If user clicked both columns by mistake, block it
    if (explicitSources.length > 1 && !allSame(explicitSources)) {
      setErrorMessage(
        "A single receipt cannot mix Patient and Medical Aid. Deselect and choose only one."
      );
      return;
    }

    // Effective payer for this *entire* receipt:
    // 1) use global paidBy if set
    // 2) else use the explicit column chosen on first selected invoice
    // 3) else block (user must choose a side)
    const effectivePaidBy = paidBy || explicitSources[0] || null;

    if (!effectivePaidBy) {
      setErrorMessage(
        'Choose "Patient" or "Medical Aid" by clicking one of the columns.'
      );
      return;
    }

    // Patient receipts require a payment method or useUnallocated
    if (effectivePaidBy === "patient" && !paymentMethod && !useUnallocated) {
      setErrorMessage("Please select a payment method.");
      return;
    }

    // Only-unallocated mode: must have a positive unallocated amount.
    if (effectivePaidBy === "patient" && useUnallocated) {
      const enteredUnalloc = parseMoney(unallocatedTendered);
      if (!paymentMethod && enteredUnalloc <= 0) {
        setErrorMessage("Enter an amount to use from unallocated funds.");
        return;
      }
      if (enteredUnalloc > openingBalance) {
        setErrorMessage(
          `Unallocated amount exceeds available unallocated funds (R ${openingBalance.toFixed(2)}).`
        );
        return;
      }
      if (selectedInvoices.length > 0 && enteredUnalloc > totalSelectedAmount) {
        setErrorMessage(
          `Unallocated amount cannot exceed the selected invoice total (R ${totalSelectedAmount.toFixed(2)}).`
        );
        return;
      }
    }

    // --- Custom allocation (no invoice selected, money goes straight to unallocated) ---
    if (
      customAllocSource &&
      selectedInvoices.length === 0 &&
      lineSelectionCount === 0
    ) {
      if (!accountSelected) {
        alert("No account selected.");
        return;
      }
      const allocAmount = parseMoney(receivedAmount);
      if (allocAmount <= 0) {
        alert("Enter a valid amount.");
        return;
      }

      const newPracticeBalance = parseFloat(
        (openingBalance + allocAmount).toFixed(2)
      );
      const existing = Array.isArray(accountSelected?.unallocated)
        ? accountSelected.unallocated
        : [];
      const updatedUnallocated = [
        ...existing.filter((u) => u.practiceNumber !== selectedPractice),
        { practiceNumber: selectedPractice, practiceBalance: newPracticeBalance },
      ];

      const receiptMethod =
        PAYMENT_METHOD_LABELS[paymentMethod] || "Patient";

      const receiptData = {
        receiptDate,
        receiptTotalAmount: allocAmount,
        receiptMethod,
        receiptPaymentMethod: receiptMethod,
        receiptReference: referenceNumber,
        receiptPracticeNumber: selectedPractice,
        accountId: accountSelected._id,
        unallocated: updatedUnallocated,
        receiptInvoices: [
          {
            invoiceNumber: "Unallocated Funds",
            paymentAmount: allocAmount,
            paymentSource: customAllocSource,
            receiptReference: referenceNumber,
            receiptDate,
            outstandingBalance: {
              patientOutstanding: newPracticeBalance,
              medAidOutstanding: 0,
              totalOutstanding: newPracticeBalance,
              paidAmount: 0,
            },
          },
        ],
      };

      setIsAllocating(true);
      try {
        const receiptCreated = await allocateReceipt(receiptData);
        setReceiptId(receiptCreated?.receiptNumber || null);
        setCreatedReceipt(receiptData);

        setAccountSelected((prev) => ({
          ...prev,
          unallocated: updatedUnallocated,
        }));

        showToast("success", "Receipt allocated successfully!");
        setShowPostReceiptModal(true);
      } catch (error) {
        console.error("Failed to allocate receipt:", error);
        setErrorMessage("Failed to save allocation, please try again.");
      } finally {
        setIsAllocating(false);
      }
      return;
    }

    if (
      !accountSelected ||
      (!selectedInvoices.length && lineSelectionCount === 0)
    ) {
      alert("Select at least one invoice and enter a valid amount.");
      return;
    }

    // --- amount to allocate ---
    const newMoney = parseMoney(receivedAmount);
    const unallocatedUsed =
      effectivePaidBy === "patient" && useUnallocated
        ? Math.min(parseMoney(unallocatedTendered), openingBalance, totalSelectedAmount)
        : 0;
    const totalReceived = newMoney + unallocatedUsed;
    if (totalReceived <= 0) {
      alert("Enter a valid amount.");
      return;
    }

    // Per-line picks are exact amounts, not a pot to spread — under-tendering
    // them would silently short a line the user explicitly priced. Block it.
    if (
      lineSelectionCount > 0 &&
      totalReceived < parseFloat((totalSelectedAmount - 0.005).toFixed(2))
    ) {
      setErrorMessage(
        `The tendered amount must cover the selected total (R ${totalSelectedAmount.toFixed(
          2
        )}) when paying specific lines. Reduce the line amounts instead.`
      );
      return;
    }

    // --- sort selected invoices oldest → newest (whole-invoice + line-mode) ---
    const sorted = [
      ...selectedInvoices.map((id) => ({ id, mode: "invoice" })),
      ...Object.keys(lineSelectionsByInvoice)
        .filter((id) => !selectedInvoices.includes(id))
        .map((id) => ({ id, mode: "lines" })),
    ]
      .map(({ id, mode }) => ({
        invoice: invoiceList.find((i) => i._id === id),
        id,
        mode,
      }))
      .filter((x) => x.invoice)
      .sort(
        (a, b) =>
          new Date(a.invoice.invoiceDate) - new Date(b.invoice.invoiceDate)
      );

    // --- allocate across invoices, forcing the same paymentSource for every line ---
    const invoiceLines = [];
    let remaining = totalReceived;

    for (const { invoice, id, mode } of sorted) {
      if (["Pending", "Paid"].includes(invoice.status)) continue;

      const bal = outstandingBalances[id] || {
        patientOutstanding: 0,
        medAidOutstanding: 0,
        totalOutstanding: 0,
        paidAmount: 0,
      };

      if (mode === "lines") {
        // Per-line allocation: pay exactly what was picked per line. A med-aid
        // remainder on a line moves to the patient (ERA principle) — the line's
        // med-aid side is settled at the paid amount.
        const sels = lineSelectionsByInvoice[id] || [];
        const linePayments = sels
          .map((sel) => {
            const amt = parseMoney(sel.amountText);
            const shortfall =
              sel.source === "medicalAid"
                ? Math.max(0, parseFloat((sel.maxAmount - amt).toFixed(2)))
                : 0;
            return {
              lineNumber: sel.lineNumber,
              paymentAmount: parseFloat(amt.toFixed(2)),
              paymentSource: sel.source,
              shortfallToPatient: shortfall,
            };
          })
          .filter((lp) => lp.paymentAmount > 0);
        if (linePayments.length === 0) continue;

        const paidSum = parseFloat(
          linePayments
            .reduce((s, lp) => s + lp.paymentAmount, 0)
            .toFixed(2)
        );
        const shortfallSum = parseFloat(
          linePayments
            .reduce((s, lp) => s + (lp.shortfallToPatient || 0), 0)
            .toFixed(2)
        );
        if (paidSum <= 0 || paidSum > remaining + 0.005) continue;

        const updatedBalance = { ...bal };
        if (effectivePaidBy === "patient") {
          updatedBalance.patientOutstanding = Math.max(
            0,
            parseFloat(((bal.patientOutstanding || 0) - paidSum).toFixed(2))
          );
        } else {
          updatedBalance.medAidOutstanding = Math.max(
            0,
            parseFloat(
              ((bal.medAidOutstanding || 0) - paidSum - shortfallSum).toFixed(2)
            )
          );
          updatedBalance.patientOutstanding = parseFloat(
            ((bal.patientOutstanding || 0) + shortfallSum).toFixed(2)
          );
        }
        updatedBalance.totalOutstanding = parseFloat(
          (
            (updatedBalance.patientOutstanding || 0) +
            (updatedBalance.medAidOutstanding || 0)
          ).toFixed(2)
        );
        updatedBalance.paidAmount = parseFloat(
          ((bal.paidAmount || 0) + paidSum).toFixed(2)
        );

        invoiceLines.push({
          _id: id,
          invoiceNumber: invoice.invoiceNumber,
          invoiceDate: invoice.invoiceDate,
          paymentAmount: paidSum,
          paymentSource: effectivePaidBy,
          receiptReference: referenceNumber,
          receiptDate,
          outstandingBalance: updatedBalance,
          lines: linePayments,
        });

        remaining = parseFloat((remaining - paidSum).toFixed(2));
        if (remaining <= 0) break;
        continue;
      }

      const due =
        effectivePaidBy === "patient"
          ? bal.patientOutstanding || 0
          : bal.medAidOutstanding || 0;

      const alloc = Math.min(due, remaining);
      if (alloc <= 0) continue;

      const updatedBalance = { ...bal };
      if (effectivePaidBy === "patient") {
        updatedBalance.patientOutstanding = Math.max(
          0,
          (bal.patientOutstanding || 0) - alloc
        );
      } else {
        updatedBalance.medAidOutstanding = Math.max(
          0,
          (bal.medAidOutstanding || 0) - alloc
        );
      }
      updatedBalance.totalOutstanding =
        (updatedBalance.patientOutstanding || 0) +
        (updatedBalance.medAidOutstanding || 0);
      updatedBalance.paidAmount = (bal.paidAmount || 0) + alloc;

      invoiceLines.push({
        _id: id,
        invoiceNumber: invoice.invoiceNumber,
        invoiceDate: invoice.invoiceDate,
        paymentAmount: alloc,
        paymentSource: effectivePaidBy, // <- single source for the whole receipt
        receiptReference: allocations[id]?.refPatient || referenceNumber,
        receiptDate,
        outstandingBalance: updatedBalance,
      });

      remaining -= alloc;
      if (remaining <= 0) break;
    }

    // --- leftover & unallocated funds handling (patient-only) ---
    const sumAllocated = invoiceLines.reduce(
      (s, l) => s + (l.paymentAmount || 0),
      0
    );
    const leftover = parseFloat((totalReceived - sumAllocated).toFixed(2));

    let newPracticeBalance = openingBalance;
    if (effectivePaidBy === "patient") {
      // "Give Change" mode: leftover is returned as cash, not added to unallocated
      const isGivingChange = paymentMethod === "cash" && !addUnallocated;
      const surplusToUnallocated = isGivingChange ? 0 : Math.max(0, leftover);
      // When cash "Add to Unallocated" with surplus, the full surplus
      // goes to unallocated without deducting the draw
      const deductUsed =
        paymentMethod === "cash" && addUnallocated && leftover > 0
          ? 0
          : unallocatedUsed;
      newPracticeBalance = parseFloat(
        (openingBalance - deductUsed + surplusToUnallocated).toFixed(2)
      );
      if (surplusToUnallocated > 0) {
        invoiceLines.push({
          invoiceNumber: "Unallocated Funds",
          invoiceDate: "",
          paymentAmount: surplusToUnallocated,
          paymentSource: "patient",
          receiptReference: referenceNumber,
          receiptDate,
          outstandingBalance: {
            patientOutstanding: newPracticeBalance,
            medAidOutstanding: 0,
            totalOutstanding: newPracticeBalance,
            paidAmount: 0,
          },
        });
      }
    } else {
      // medical aid side: add any leftover to unallocated
      newPracticeBalance = parseFloat(
        (openingBalance + Math.max(0, leftover)).toFixed(2)
      );
      if (leftover > 0) {
        invoiceLines.push({
          invoiceNumber: "Unallocated Funds",
          invoiceDate: "",
          paymentAmount: leftover,
          paymentSource: "medicalAid",
          receiptReference: referenceNumber,
          receiptDate,
          outstandingBalance: {
            patientOutstanding: newPracticeBalance,
            medAidOutstanding: 0,
            totalOutstanding: newPracticeBalance,
            paidAmount: 0,
          },
        });
      }
    }

    const existing = Array.isArray(accountSelected?.unallocated)
      ? accountSelected.unallocated
      : [];
    const updatedUnallocated = [
      ...existing.filter((u) => u.practiceNumber !== selectedPractice),
      { practiceNumber: selectedPractice, practiceBalance: newPracticeBalance },
    ];

    // --- receipt method: single source, no mixing ---
    const receiptMethod =
      effectivePaidBy === "medicalAid"
        ? "medicalAid"
        : PAYMENT_METHOD_LABELS[paymentMethod] || (useUnallocated ? "Unallocated Funds" : "Patient");

    const receiptData = {
      receiptDate,
      receiptTotalAmount: totalReceived,
      receiptMethod, // <- saved to DB
      receiptPaymentMethod: receiptMethod, // <- keep for PDF/back-compat
      receiptReference: referenceNumber,
      receiptPracticeNumber: selectedPractice,
      accountId: accountSelected._id,
      unallocated: updatedUnallocated,
      receiptInvoices: invoiceLines,
      unallocatedDrawn: unallocatedUsed,
    };

    setIsAllocating(true);
    try {
      const receiptCreated = await allocateReceipt(receiptData);

      setReceiptId(receiptCreated?.receiptNumber || null);
      setCreatedReceipt(receiptData);

      // update balances in UI
      setOutstandingBalances((prev) => {
        const copy = { ...prev };
        invoiceLines.forEach((ln) => {
          if (ln.invoiceNumber === "Unallocated Funds") return;
          const bal = copy[ln._id];
          if (!bal) return;
          Object.assign(bal, ln.outstandingBalance);
          if (bal.totalOutstanding === 0) bal.status = "Paid";
          else if ((bal.paidAmount || 0) > 0) bal.status = "Partially Paid";
        });
        return copy;
      });

      // Use server-recomputed balance if available; fall back to client-computed value
      const serverBalance = receiptCreated?.newPracticeBalance;
      const finalUnallocated =
        serverBalance != null
          ? [
              ...(accountSelected?.unallocated || []).filter(
                (u) => u.practiceNumber !== selectedPractice
              ),
              { practiceNumber: selectedPractice, practiceBalance: serverBalance },
            ]
          : updatedUnallocated;

      setAccountSelected((prev) => ({
        ...prev,
        unallocated: finalUnallocated,
      }));

      showToast("success", "Receipt allocated successfully!");
      setShowPostReceiptModal(true);
    } catch (error) {
      console.error("Failed to allocate receipt:", error);
      setErrorMessage("Failed to save allocation, please try again.");
    } finally {
      setIsAllocating(false);
    }
  }, [
    isAllocating,
    paidBy,
    paymentMethod,
    addUnallocated,
    useUnallocated,
    unallocatedTendered,
    customAllocSource,
    selectedPaymentSources,
    selectedInvoices,
    selectedLineCells,
    lineSelectionsByInvoice,
    lineSelectionCount,
    totalSelectedAmount,
    invoiceList,
    outstandingBalances,
    allocations,
    receivedAmount,
    openingBalance,
    referenceNumber,
    receiptDate,
    selectedPractice,
    accountSelected,
    setErrorMessage,
    showToast,
    setShowPostReceiptModal,
    setReceiptId,
    setCreatedReceipt,
    setOutstandingBalances,
    setAccountSelected,
  ]);

  // Set allocateRef
  useEffect(() => {
    allocateRef.current = handleAllocate;
  }, [handleAllocate]);

  const handleOzowLink = async () => {
    setLoading(true);
    try {
      const response = await sendOzowLink({
        practiceNumber: selectedPractice,
        accountFirstName: accountSelected.accountFirstName,
        accountLastName: accountSelected.accountLastName,
        email,
        totalSelectedAmount,
        transactionRef: accountSelected._id + String(totalSelectedAmount),
      });

      if (response?.emailSent) {
        setEmailLink(true);
        setPaymentLinkReference(response?.paymentRequestId);

        const lineAmounts = {};
        Object.entries(lineSelectionsByInvoice).forEach(([id, sels]) => {
          lineAmounts[id] = parseFloat(
            sels
              .reduce((s, sel) => s + parseMoneyValue(sel.amountText), 0)
              .toFixed(2)
          );
        });
        const payload = {
          paymentRequestId: response?.paymentRequestId,
          transactionReference:
            accountSelected._id + String(totalSelectedAmount),
          amount: totalSelectedAmount,
          invoiceIds: allSelectedInvoiceIds,
          invoiceAmounts: { ...selectedInvoiceAmounts, ...lineAmounts },
          accountId: accountSelected._id,
          practiceNumber: selectedPractice,
        };
        await createPaymentLink(payload);
      }
    } catch (error) {
      console.error("Error sending Ozow link:", error);
    } finally {
      setLoading(false);
      showToast("success", "The payment link was sent successfully");
      setTimeout(() => {
        onHide();
        navigate(0);
      }, 3000);
    }
  };

  const startYocoPayment = async () => {
    try {
      setYocoBusy(true);
      setYocoError("");

      if (!selectedYocoDevice) {
        setYocoError("Select a Yoco device.");
        return;
      }
      if (!allSelectedInvoiceIds.length) {
        setYocoError("Select at least one invoice.");
        return;
      }
      if (paidBy !== "patient") {
        setYocoError(
          'For card payments, click the "Patient" column to set Paid by = Patient.'
        );
        return;
      }

      const cents = Math.round((totalSelectedAmount || 0) * 100);
      if (!cents || cents <= 0) {
        setYocoError("Nothing to pay. Select invoice amounts first.");
        return;
      }

      const clientReference = `INV-${
        accountSelected?.accountNumber || "ACC"
      }-${Date.now()}`;
      const resp = await createWebposPayment(selectedYocoDevice, {
        practiceNumber: selectedPractice,
        amountCents: cents,
        clientReference,
        metadata: {
          accountId: String(accountSelected?._id || ""),
          // choose one style you prefer:
          // 1) CSV (easy to read)
          invoiceIds: allSelectedInvoiceIds.map(String).join(","),
          // or 2) JSON string (lossless)
          // invoiceIds: JSON.stringify(selectedInvoices.map(String)),
        },
      });

      const { paymentId, redirectUrl } = resp || {};
      if (!redirectUrl || !paymentId) {
        setYocoError("Failed to create Yoco payment.");
        return;
      }

      setYocoPaymentId(paymentId);
      setYocoRedirectUrl(redirectUrl);
      setShowYocoIframe(true);
      setPaymentMethod("creditCard");
      setReferenceNumber(`Yoco ${paymentId}`);
    } catch (e) {
      console.error("Start Yoco payment error:", e.response?.data || e.message);
      setYocoError(e.response?.data?.error || "Failed to start Yoco payment.");
    } finally {
      setYocoBusy(false);
    }
  };

  const handlePrintReceipt = async () => {
    if (!createdReceipt || !receiptId) {
      console.warn("No receipt data available for printing");
      return;
    }
    try {
      const rData = await getReceiptByNumber(receiptId);
      const receiptData = {
        receiptNumber: rData.receiptNumber,
        receiptDate:
          rData.receiptDate || new Date().toISOString().split("T")[0],
        receiptTotalAmount: rData.receiptTotalAmount || 0,
        receiptInvoices: rData.receiptInvoices || [],
        receiptPaymentMethod: rData.receiptPaymentMethod || paymentMethod || "",
        receiptReference:
          rData.receiptInvoices[0]?.receiptReference || referenceNumber || "",
        practice: fullPractice,
        account: accountSelected,
      };

      await ExportPDFReceipt(receiptData);
    } catch (error) {
      console.error("Error printing receipt:", error);
      alert("Failed to generate PDF. Please try again.");
    }
  };

  const handleDownloadSelected = async () => {
    if (!includeReceipt && !includeStatement) return;
    try {
      if (includeReceipt) {
        await handlePrintReceipt();
      }
      if (includeStatement) {
        const todayStr = new Date().toISOString().split("T")[0];
        const startStr = computeStatementStartDate();
        const practiceNum = selectedPractice || fullPractice?.practiceNumber;
        if (!accountSelected?._id || !practiceNum) {
          alert("Cannot generate statement: missing account or practice information.");
        } else {
          const blob = await getStatementPDF(
            String(accountSelected._id),
            startStr,
            todayStr,
            [String(practiceNum)]
          );
          const blobURL = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = blobURL;
          // Initial_Surname_Statement_start_to_end, e.g.
          // "C_Cilliers_Statement_2026-01-01_to_2026-01-31.pdf" (matches the
          // backend buildStatementFileName in statementPDF.js).
          const initial = String(accountSelected?.accountFullNames || "")
            .trim()
            .charAt(0)
            .toUpperCase();
          const surname = String(accountSelected?.accountLastName || "").trim();
          const namePart =
            [initial, surname].filter(Boolean).join("_") || "Account";
          const safeName = namePart.replace(/[^a-zA-Z0-9_-]/g, "_");
          link.download = `${safeName}_Statement_${startStr}_to_${todayStr}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(blobURL);
        }
      }
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download. Please try again.");
    } finally {
      setShowPostReceiptModal(false);
      navigate(0);
    }
  };

  const prepareEmailReceipt = async () => {
    if (!createdReceipt || !receiptId) {
      console.warn("No receipt data available for emailing");
      return;
    }

    try {
      const rData = await getReceiptByNumber(receiptId);
      const receiptData = {
        receiptNumber: rData?.receiptNumber,
        receiptDate:
          rData?.receiptDate || new Date().toISOString().split("T")[0],
        receiptTotalAmount: rData?.receiptTotalAmount || 0,
        receiptInvoices: rData?.receiptInvoices || [],
        receiptPaymentMethod: rData?.receiptMethod || "",
        receiptReference: rData?.receiptInvoices?.[0]?.receiptReference || "",
        practice: fullPractice,
        account: accountSelected,
      };

      const blob = await ExportPDFReceipt(receiptData, { asBlob: true });
      if (blob) {
        setReceiptBlob(blob);
        const primary = accountSelected?.accountEmail || account?.accountEmail || "";

setReceiptEmailPrimary(primary);

// show all account emails that exist (open by default)
setReceiptExtraEmails({
  email2: accountSelected?.accountEmail2 ? accountSelected.accountEmail2 : undefined,
  email3: accountSelected?.accountEmail3 ? accountSelected.accountEmail3 : undefined,
  email4: accountSelected?.accountEmail4 ? accountSelected.accountEmail4 : undefined,
  email5: accountSelected?.accountEmail5 ? accountSelected.accountEmail5 : undefined,
});

// (optional) keep your old state if anything else still references it
setEmailToSend(primary);

setShowEmailFields(true);

        setReceiptEmailData(
          buildReceiptEmailData({
            receipt: {
              receiptNumber: receiptData.receiptNumber,
              receiptDate: receiptData.receiptDate,
            },
            account: accountSelected,
            practice: fullPractice,
          })
        );
      }
    } catch (error) {
      console.error("Error preparing receipt email:", error);
    }
  };

 const handleSendEmail = () => {
    const primary = (receiptEmailPrimary || "").trim();

    const extras = {
      email2: receiptExtraEmails.email2?.trim() || "",
      email3: receiptExtraEmails.email3?.trim() || "",
      email4: receiptExtraEmails.email4?.trim() || "",
      email5: receiptExtraEmails.email5?.trim() || "",
    };

    if (!primary && !extras.email2 && !extras.email3 && !extras.email4 && !extras.email5) {
      alert("Please enter at least one email address.");
      return;
    }

    // Hide modals visually (component stays mounted so toast can display)
    setShowPostReceiptModal(false);
    setShowEmailFields(false);
    setEmailSending(true);

    const emailList = [primary, extras.email2, extras.email3, extras.email4, extras.email5].filter(Boolean);

    const sends = [];

    if (includeReceipt && receiptBlob) {
      sends.push(
        sendInvoiceByEmail({
          emails: emailList,
          pdfBlob: receiptBlob,
          type: "receipt",
          accountId: accountSelected._id,
          emailData: receiptEmailData,
        })
      );
    }

    if (includeStatement) {
      const todayStr = new Date().toISOString().split("T")[0];
      const startStr = computeStatementStartDate();
      const practiceNum = selectedPractice || fullPractice?.practiceNumber;
      if (accountSelected?._id && practiceNum) {
        // Route the statement email through /statements/sendStatementByEmail
        // (same path AccountStatements.jsx uses) instead of the generic
        // /invoices/sendInvoiceByEmail. The statement handler:
        //   • derives the period from the actual invoice data and shows it
        //     in the email subject + body ("Your Statement of Account
        //     (YYYY-MM-DD to YYYY-MM-DD)" / "for the period <start> to <end>")
        //   • renders aged totals (patient + medical aid) in the body
        //   • uses the practice's saved finComms.statement template if present
        // Receipt.jsx previously used the invoice handler with a hardcoded
        // 1900-01-01 start, so the email body never showed a period.
        sends.push((async () => {
          const accountIdStr = String(accountSelected._id);
          const practiceNumbers = [String(practiceNum)];
          const [statBlob, statementData] = await Promise.all([
            getStatementPDF(accountIdStr, startStr, todayStr, practiceNumbers),
            getStatementToExport(accountIdStr, startStr, todayStr, practiceNumbers),
          ]);
          const formData = new FormData();
          formData.append("email",  emailList[0] || "");
          formData.append("email2", emailList[1] || "");
          formData.append("email3", emailList[2] || "");
          formData.append("email4", emailList[3] || "");
          formData.append("email5", emailList[4] || "");
          formData.append("statementData", JSON.stringify(statementData));
          formData.append("pdf", statBlob, "Statement.pdf");
          return sendStatementByEmail(formData);
        })());
      }
    }

    Promise.all(sends)
      .then(() => {
        const sentItems = [includeReceipt && "receipt", includeStatement && "statement"]
          .filter(Boolean)
          .join(" and ");
        showToast("success", `${sentItems.charAt(0).toUpperCase() + sentItems.slice(1)} emailed successfully!`);
      })
      .catch((error) => {
        console.error("Error sending email:", error);
        showToast("error", "Failed to send email");
      })
      .finally(() => {
        setTimeout(() => {
          setEmailSending(false);
          onHide();
        }, 2500);
      });
  };


  const handleClose = () => setCloseAlert(true);
  const handleCancelNo = () => setCloseAlert(false);
  const handleCancelYes = () => {
    setCloseAlert(false);
    onHide();
  };

  const isCellSelected = (invoiceId, source) => {
    return (
      paidBy === source &&
      selectedInvoices.includes(invoiceId) &&
      selectedPaymentSources[invoiceId] === source
    );
  };

  if (!show) return null;

  return (
    <>
      <Modal show={show && !emailSending} onHide={handleClose} dialogClassName="modal-85w">
        <Modal.Header
          closeButton={!closeAlert}
          style={{ background: "#CFE2FF" }}
          className="rounded-0 text-primary d-flex align-items-center"
        >
          <Modal.Title as="h6" className="m-0">
            <i className="fa-light fa-receipt me-2" />
            Receipt
          </Modal.Title>
        </Modal.Header>

        {closeAlert && (
          <Alert
            variant="info"
            className="w-100 d-flex align-items-center justify-content-between mx-0 mb-0 fs-6 fw-normal border-0 rounded-0"
          >
            <span>
              Are you sure you want to close? Changes will not be saved.
            </span>
            <div className="ms-auto d-flex">
              <Button
                variant="outline-primary"
                className="me-1"
                onClick={handleCancelYes}
              >
                <FaCheck />
              </Button>
              <Button
                variant="outline-secondary"
                className="me-1"
                onClick={handleCancelNo}
              >
                <FaTimes />
              </Button>
            </div>
          </Alert>
        )}

        {errorMessage && (
          <Alert
            variant="info"
            className="w-100 d-flex align-items-center justify-content-between mx-0 mb-0 fs-6 fw-normal border-0 rounded-0"
          >
            <span>{errorMessage}</span>
            <div className="ms-auto d-flex">
              <Button
                variant="outline-primary"
                className="me-1"
                onClick={() => setErrorMessage("")}
              >
                <FaCheck />
              </Button>
            </div>
          </Alert>
        )}

        <Modal.Body>
          <Form>
            {!accountSelected ? (
              <>
                <h6 className="text-primary mb-2">Search for an Account</h6>
                <SearchAccounts onAccountSelect={setAccountSelected} />
              </>
            ) : (
              <>
                <Row>
                  {/* Left Column: Account Info */}
                  <Col md={6} className="border-end pe-4">
                    <h5 className="text-primary mb-1">
                      {accountSelected.accountFullNames}{" "}
                      {accountSelected.accountLastName}
                    </h5>
                    <Row>
                      <Col md={6} className="mb-1">
                        <p>
                          <strong>Account No:</strong>{" "}
                          {accountSelected.accountNumber}
                        </p>
                        <p>
                          {accountSelected.accountPostalAddress1}
                          <br />
                          {accountSelected.accountPostalAddress2}
                          <br />
                          {accountSelected.accountPostalAddressCity}
                          <br />
                          {accountSelected.accountPostalAddressPostalCode}
                          <br />
                          {accountSelected.accountEmail}
                          <br />
                          {accountSelected.accountTelNumberCell}
                          <br />
                        </p>
                      </Col>
                      <Col md={6} className="mb-1">
                        <Row>
                          <Col md={6}>
                            <p>
                              <strong>Medical Aid:</strong>
                            </p>
                          </Col>
                          <Col md={6}>
                            {accountSelected.accountMedicalAid ? (
                              <p>
                                {accountSelected.accountMedicalAid}
                                <br />
                                {accountSelected.accountMedicalAidNumber}
                                <br />
                                {accountSelected.accountMedicalAidPlan}
                                <br />
                                {accountSelected.accountMedicalAidOption}
                                <br />
                              </p>
                            ) : (
                              <p className="text-muted">Private</p>
                            )}
                          </Col>
                        </Row>
                      </Col>
                    </Row>

                    {/* Paid by and Paid On */}
                    <Row className="mb-2 align-items-center">
                      <Col xs="auto">
                        <span className="fw-bold">Paid by:</span>{" "}
                        {paidBy ? (
                          <span className="fw-bold text-primary">
                            {paidBy === "patient" ? "Patient" : "Medical Aid"}
                          </span>
                        ) : (
                          <span className="text-muted">
                            Select an invoice amount
                          </span>
                        )}
                      </Col>
                      <Col xs="auto" className="ms-auto d-flex align-items-center gap-2">
                        <span className="fw-bold">Paid On:</span>
                        <Form.Control
                          type="date"
                          size="sm"
                          value={receiptDate}
                          onChange={(e) => setReceiptDate(e.target.value)}
                          style={{ width: "auto" }}
                        />
                      </Col>
                    </Row>

                    {/* Payment Methods */}
                    {paidBy === "patient" && (
                      <>
                        <Row className="ms-1 mt-2 gap-1">
                          {[
                            "cash",
                            "creditCard",
                            "paymentLink",
                            "eft",
                          ]
                            .filter((method) => {
                              if (method !== "paymentLink") return true;
                              const hasOzow =
                                bankingDetails?.ozow?.paymentLink === "true";
                              const hasYoco =
                                bankingDetails?.yoco?.paymentLink === "true";
                              return hasOzow || hasYoco;
                            })
                            .map((method) => (
                              <Button
                                key={method}
                                variant={
                                  paymentMethod === method
                                    ? "primary"
                                    : "outline-secondary"
                                }
                                name={method}
                                onClick={handlePaymentMethod}
                                style={{ width: "18%" }}
                              >
                                {method === "cash" && "Cash"}
                                {method === "creditCard" && "Card"}
                                {method === "paymentLink" && "Link"}
                                {method === "eft" && "EFT"}
                              </Button>
                            ))}
                          {openingBalance > 0 && !customAllocSource && (
                            <Button
                              variant={useUnallocated ? "success" : "outline-success"}
                              onClick={() => setUseUnallocated((prev) => !prev)}
                              style={{ width: "20%" }}
                              className="d-flex flex-column align-items-center justify-content-center py-1"
                            >
                              <span>Unallocated</span>
                              <small style={{ fontSize: "0.75em" }}>
                                R {formatCurrency(openingBalance)}
                              </small>
                            </Button>
                          )}
                        </Row>
                      </>
                    )}

                    {/* Yoco Credit Card Section */}
                    {paidBy === "patient" && paymentMethod === "creditCard" && (
                      <>
                        {yocoDevices.length > 0 ? (
                          <Row className="mt-3 align-items-end">
                            <Col xs={4} className="fw-bold">
                              Yoco Web POS:
                            </Col>
                            <Col xs={5}>
                              <Form.Select
                                size="sm"
                                value={selectedYocoDevice}
                                onChange={(e) =>
                                  setSelectedYocoDevice(e.target.value)
                                }
                                disabled={yocoBusy || showYocoIframe}
                              >
                                {yocoDevices.map((d) => (
                                  <option key={d.id} value={d.id}>
                                    {d.name || d.id}
                                  </option>
                                ))}
                              </Form.Select>
                            </Col>
                            <Col xs={3} className="text-end">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={startYocoPayment}
                                disabled={yocoBusy || !totalSelectedAmount}
                              >
                                {yocoBusy ? (
                                  <Spinner
                                    animation="border"
                                    size="sm"
                                    role="status"
                                  />
                                ) : (
                                  "Start Yoco payment"
                                )}
                              </Button>
                            </Col>
                          </Row>
                        ) : (
                          <Row className="mt-3">
                            <Col>
                              <Alert variant="warning" className="py-1 mb-0">
                                No Yoco Web POS devices for this practice.
                              </Alert>
                            </Col>
                          </Row>
                        )}
                        {yocoError && (
                          <Row className="mt-2">
                            <Col>
                              <Alert variant="danger" className="py-1 mb-0">
                                {yocoError}
                              </Alert>
                            </Col>
                          </Row>
                        )}
                      </>
                    )}

                    {/* Amount Info */}
                    <Row className="mt-3 align-items-center">
                      <Col xs={4} className="fw-bold">
                        Total Selected:
                      </Col>
                      <Col xs={4} className="text-end">
                        R {formatCurrency(totalSelectedAmount)}
                      </Col>
                    </Row>

                    <Row className="mt-2 align-items-center">
                      <Col xs={4} className="fw-bold">
                        {tenderedLabel}:
                      </Col>
                      <Col xs={4}>
                        <Form.Control
                          className="text-end"
                          size="sm"
                          name="receivedAmount"
                          type="text"
                          value={onlyUnallocated ? "" : receivedAmount}
                          onChange={handleChange}
                          readOnly={!!customAllocSource || onlyUnallocated}
                          disabled={onlyUnallocated}
                          placeholder={onlyUnallocated ? "Paying from unallocated only" : ""}
                        />
                      </Col>
                    </Row>

                    {useUnallocated && (
                      <Row className="mt-2 align-items-center">
                        <Col xs={4} className="fw-bold">
                          Unallocated Tendered:
                        </Col>
                        <Col xs={4}>
                          <Form.Control
                            className="text-end"
                            size="sm"
                            name="unallocatedTendered"
                            type="text"
                            value={unallocatedTendered}
                            onChange={handleChange}
                            title={`Max: R ${Math.min(openingBalance, totalSelectedAmount).toFixed(2)}`}
                          />
                        </Col>
                      </Row>
                    )}

                    {/* Cash Options */}
                    {paymentMethod === "cash" && !useUnallocated && totalTendered > totalSelectedAmount && (
                      <>
                        <Row className="mt-2 align-items-center">
                          <Col xs={4} className="fw-bold">
                            Cash Option:
                          </Col>
                          <Col xs={8}>
                            <Form.Check
                              inline
                              type="radio"
                              id="cashOptionGiveChange"
                              label="Give Change"
                              name="cashOption"
                              checked={!addUnallocated}
                              onChange={() => setAddUnallocated(false)}
                            />
                            <Form.Check
                              inline
                              type="radio"
                              id="cashOptionAddToUnallocated"
                              label="Add to Unallocated"
                              name="cashOption"
                              checked={addUnallocated}
                              onChange={() => setAddUnallocated(true)}
                            />
                          </Col>
                        </Row>
                        {!addUnallocated && changeAmount > 0 && (
                          <Row className="mt-2 align-items-center">
                            <Col xs={4} className="fw-bold">
                              Change:
                            </Col>
                            <Col xs={4}>
                              <div className="d-flex align-items-center">
                                <Form.Control
                                  className="text-end"
                                  size="sm"
                                  type="text"
                                  value={`R ${formatCurrency(changeAmount)}`}
                                  disabled
                                />
                                <span className="badge bg-success ms-2">
                                  Change Due
                                </span>
                              </div>
                            </Col>
                          </Row>
                        )}
                      </>
                    )}

                    <hr className="my-2" />

                    <Row className="align-items-center">
                      <Col xs={4} className="fw-bold">
                        Total Outstanding:
                      </Col>
                      <Col xs={4} className="text-end">
                        R {formatCurrency(totalOutstandingAfter)}
                      </Col>
                    </Row>

                    <Row className="mt-2 align-items-center">
                      <Col xs={4} className="fw-bold">
                        Unallocated Closing Balance:
                      </Col>
                      <Col xs={4} className="text-end">
                        R {formatCurrency(closingUnallocated)}
                      </Col>
                    </Row>

                    {/* Payment Link Fields */}
                    {paymentMethod === "paymentLink" && (
                      <>
                        <Row className="mt-2 align-items-center">
                          <Col xs={4} className="fw-bold">
                            Email Link To:
                          </Col>
                          <Col xs={6}>
                            <Form.Control
                              size="sm"
                              type="text"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                            />
                          </Col>
                        </Row>
                        <Row className="mt-2 align-items-center">
                          <Col xs={4} className="fw-bold">
                            Payment Link Reference:
                          </Col>
                          <Col xs={6}>
                            <Form.Control
                              size="sm"
                              type="text"
                              value={paymentLinkReference}
                              disabled
                            />
                          </Col>
                        </Row>
                      </>
                    )}

                    {paymentMethod !== "paymentLink" && (
                      <Row className="mt-2 align-items-center">
                        <Col xs={4} className="fw-bold">
                          Reference Number:
                        </Col>
                        <Col xs={6}>
                          <Form.Control
                            type="text"
                            placeholder="Enter payment reference"
                            value={referenceNumber}
                            onChange={(e) => setReferenceNumber(e.target.value)}
                          />
                        </Col>
                      </Row>
                    )}
                  </Col>

                  {/* Right Column: Invoice Table or Yoco iframe */}
                  <Col md={6}>
                    {/* —— Practice selector —— */}
                    {practiceOptions.length > 0 && (
                      <Form.Group as={Row} className="mb-2 align-items-center">
                        <Form.Label column sm={2} className="pt-1">
                          Practice:
                        </Form.Label>
                        <Col sm={6}>
                          <Form.Select
                            size="sm"
                            value={selectedPractice}
                            onChange={(e) =>
                              setSelectedPractice(e.target.value)
                            }
                            disabled={
                              allSelectedInvoiceIds.length > 0 || showYocoIframe
                            } // prevent mid-allocation switches
                          >
                            {practiceOptions.map((p) => (
                              <option
                                key={p.practiceNumber}
                                value={p.practiceNumber}
                              >
                                {p.practiceName}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>

                        {(allSelectedInvoiceIds.length > 0 || showYocoIframe) && (
                          <Col sm={4} className="text-end">
                            <small className="text-muted">
                              Clear selections to change
                            </small>
                          </Col>
                        )}
                      </Form.Group>
                    )}

                    <p className="text-muted mt-2">
                      Click the <strong>Patient</strong> or{" "}
                      <strong>Medical Aid</strong> amount to select a whole
                      invoice, or expand an invoice to pay specific lines —
                      click a line amount to pay it in full, or type a custom
                      amount. The unpaid remainder of a med-aid line moves to
                      the patient.
                    </p>

                    {showYocoIframe ? (
                      <div className="mt-2">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="mb-0 text-primary">Yoco Payment</h6>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setShowYocoIframe(false)}
                          >
                            Cancel
                          </Button>
                        </div>
                        <div style={{ border: "1px solid #ddd", height: 520 }}>
                          {yocoRedirectUrl ? (
                            <iframe
                              title="Yoco Web POS"
                              src={yocoRedirectUrl}
                              style={{
                                width: "100%",
                                height: "100%",
                                border: 0,
                              }}
                              allow="payment *; clipboard-write"
                            />
                          ) : (
                            <div className="d-flex h-100 align-items-center justify-content-center">
                              <Spinner animation="border" />
                            </div>
                          )}
                        </div>
                        <small className="text-muted d-block mt-2">
                          When the customer finishes and taps "Done", we'll
                          allocate this payment automatically.
                        </small>
                      </div>
                    ) : (
                      <>
                        {invoiceList.length === 0 && (
                          <p className="text-muted mb-1">
                            No open invoices for this account — you can still
                            add an amount to the account as unallocated funds
                            using the row below.
                          </p>
                        )}
                        <div className="acc-list acc-list-bare">
                        <div className="d-none d-lg-block">
                        <Table bordered hover responsive size="sm" className="mt-2">
                            <thead className="text-center">
                              <tr>
                                <th style={{ width: "4%" }} />
                                <th className="text-center align-middle text-primary">Date</th>
                                <th className="text-center align-middle text-primary">Invoice #</th>
                                <th className="text-center align-middle text-primary">Status</th>
                                <th className="text-center align-middle text-primary" style={{ width: "15%" }}>Patient</th>
                                <th className="text-center align-middle text-primary" style={{ width: "15%" }}>Med Aid</th>
                                <th className="text-center align-middle text-primary" style={{ width: "15%" }}>Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {invoiceList
                                .filter(
                                  (inv) =>
                                    inv.practice?.practiceNumber ===
                                      selectedPractice &&
                                    !["Paid", "Pending"].includes(inv.status)
                                )
                                .map((inv) => {
                                  const isDisabled = [
                                    "Pending",
                                    "Paid",
                                  ].includes(inv.status);
                                  const balance =
                                    outstandingBalances[inv._id] || {};
                                  const patientAmount =
                                    balance.patientOutstanding || 0;
                                  const medAidAmount =
                                    balance.medAidOutstanding || 0;
                                  const totalOutstanding =
                                    balance.totalOutstanding || 0;
                                  const isExpanded = expandedInvoices.has(
                                    inv._id
                                  );
                                  const invLineSels =
                                    lineSelectionsByInvoice[inv._id] || [];
                                  const hasLines = (inv.lines || []).length > 0;
                                  const perLineBlocked =
                                    hasLines && !canPayPerLine(inv);
                                  const expandProps =
                                    hasLines && !perLineBlocked
                                      ? {
                                          style: { cursor: "pointer" },
                                          onClick: () =>
                                            toggleInvoiceExpand(inv._id),
                                          title: isExpanded
                                            ? "Hide lines"
                                            : "Show lines to pay per line",
                                        }
                                      : {};

                                  return (
                                    <React.Fragment key={inv._id}>
                                    <tr
                                      className={`${
                                        isDisabled ? "disabled-row" : ""
                                      } ${
                                        invLineSels.length > 0
                                          ? "fw-semibold"
                                          : ""
                                      }`}
                                    >
                                      <td
                                        className="text-center align-middle"
                                        {...expandProps}
                                      >
                                        {hasLines && !perLineBlocked && (
                                          <i
                                            className={`fa-solid fa-chevron-${
                                              isExpanded ? "down" : "right"
                                            } text-primary`}
                                          />
                                        )}
                                        {perLineBlocked && (
                                          <OverlayTrigger
                                            placement="top"
                                            overlay={
                                              <Tooltip>
                                                {PER_LINE_BLOCKED_MSG}
                                              </Tooltip>
                                            }
                                          >
                                            <i className="fa-solid fa-chevron-right text-muted" />
                                          </OverlayTrigger>
                                        )}
                                      </td>
                                      <td
                                        className={
                                          isDisabled
                                            ? "disabled-row text-muted"
                                            : ""
                                        }
                                        {...expandProps}
                                      >
                                        {new Date(
                                          inv.invoiceDate
                                        ).toLocaleDateString()}
                                      </td>
                                      <td
                                        className={
                                          isDisabled
                                            ? "disabled-row text-muted"
                                            : ""
                                        }
                                        {...expandProps}
                                      >
                                        {inv.invoiceNumber}
                                      </td>
                                      <td
                                        className={
                                          isDisabled
                                            ? "disabled-row text-muted"
                                            : ""
                                        }
                                        {...expandProps}
                                      >
                                        {inv.status}
                                      </td>
                                      <td
                                        className={`acc-list-num ${
                                          isDisabled
                                            ? "disabled-row text-muted"
                                            : "cursor-pointer"
                                        } ${
                                          isCellSelected(inv._id, "patient")
                                            ? "bg-primary text-white"
                                            : ""
                                        }`}
                                        onClick={() =>
                                          handleCellClick(
                                            inv._id,
                                            "patient",
                                            patientAmount
                                          )
                                        }
                                        style={{
                                          cursor:
                                            isDisabled || patientAmount <= 0
                                              ? "default"
                                              : "pointer",
                                        }}
                                      >
                                        {patientAmount > 0
                                          ? `R ${formatCurrency(patientAmount)}`
                                          : ""}
                                      </td>
                                      <td
                                        className={`acc-list-num ${
                                          isDisabled
                                            ? "disabled-row text-muted"
                                            : "cursor-pointer"
                                        } ${
                                          isCellSelected(inv._id, "medicalAid")
                                            ? "bg-primary text-white"
                                            : ""
                                        }`}
                                        onClick={() =>
                                          handleCellClick(
                                            inv._id,
                                            "medicalAid",
                                            medAidAmount
                                          )
                                        }
                                        style={{
                                          cursor:
                                            isDisabled || medAidAmount <= 0
                                              ? "default"
                                              : "pointer",
                                        }}
                                      >
                                        {medAidAmount > 0
                                          ? `R ${formatCurrency(medAidAmount)}`
                                          : ""}
                                      </td>
                                      <td
                                        className={
                                          isDisabled
                                            ? "disabled-row text-muted acc-list-num"
                                            : "acc-list-num"
                                        }
                                      >
                                        {`R ${formatCurrency(totalOutstanding)}`}
                                      </td>
                                    </tr>
                                    {isExpanded &&
                                      (inv.lines || []).map((line) => {
                                        const key = `${inv._id}_${line.lineNumber}`;
                                        const sel = selectedLineCells[key];
                                        const patOut = lineOutstanding(
                                          line,
                                          "patient"
                                        );
                                        const medOut = lineOutstanding(
                                          line,
                                          "medicalAid"
                                        );
                                        const desc = line.description || "";
                                        const truncatedDesc =
                                          desc.length > 48
                                            ? desc.slice(0, 48) + "…"
                                            : desc;
                                        const selAmount = sel
                                          ? parseMoneyValue(sel.amountText)
                                          : 0;
                                        const shiftToPatient =
                                          sel &&
                                          sel.source === "medicalAid" &&
                                          selAmount < sel.maxAmount
                                            ? parseFloat(
                                                (
                                                  sel.maxAmount - selAmount
                                                ).toFixed(2)
                                              )
                                            : 0;

                                        const renderLineCell = (source, out) => {
                                          const isSel =
                                            sel && sel.source === source;
                                          const blocked =
                                            isDisabled ||
                                            !!customAllocSource ||
                                            (paidBy && paidBy !== source);
                                          if (isSel) {
                                            return (
                                              <td className="acc-list-num bg-primary text-white p-0">
                                                <div className="d-flex align-items-center">
                                                  <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                      <Tooltip>
                                                        Deselect line
                                                      </Tooltip>
                                                    }
                                                  >
                                                    <Button
                                                      variant="link"
                                                      className="text-white text-decoration-none p-0 ms-1 border-0"
                                                      style={{
                                                        width: 18,
                                                        height: 18,
                                                        lineHeight: 1,
                                                      }}
                                                      onClick={() =>
                                                        toggleLineCell(
                                                          inv._id,
                                                          line,
                                                          source
                                                        )
                                                      }
                                                    >
                                                      <i className="fa-solid fa-xmark" />
                                                    </Button>
                                                  </OverlayTrigger>
                                                  <input
                                                    type="text"
                                                    value={sel.amountText}
                                                    onChange={(e) =>
                                                      handleLineAmountChange(
                                                        key,
                                                        e.target.value
                                                      )
                                                    }
                                                    onBlur={() =>
                                                      handleLineAmountBlur(key)
                                                    }
                                                    style={{
                                                      border: "none",
                                                      outline: "none",
                                                      background:
                                                        "transparent",
                                                      width: "100%",
                                                      textAlign: "right",
                                                      fontSize: "inherit",
                                                      padding: "0 0.25rem",
                                                      color: "white",
                                                    }}
                                                  />
                                                </div>
                                              </td>
                                            );
                                          }
                                          return (
                                            <td
                                              className={`acc-list-num ${
                                                out > 0 && !blocked
                                                  ? "cursor-pointer"
                                                  : "text-muted"
                                              }`}
                                              style={{
                                                cursor:
                                                  out > 0 && !blocked
                                                    ? "pointer"
                                                    : "default",
                                              }}
                                              onClick={() =>
                                                out > 0 &&
                                                !blocked &&
                                                toggleLineCell(
                                                  inv._id,
                                                  line,
                                                  source
                                                )
                                              }
                                            >
                                              {out > 0
                                                ? `R ${formatCurrency(out)}`
                                                : ""}
                                            </td>
                                          );
                                        };

                                        return (
                                          <tr key={key}>
                                            <td />
                                            <td
                                              colSpan={3}
                                              className="ps-4 text-start small"
                                              title={desc}
                                            >
                                              {truncatedDesc}
                                              {line.tariffCode &&
                                                ` — ${line.tariffCode}`}
                                            </td>
                                            {renderLineCell(
                                              "patient",
                                              patOut
                                            )}
                                            {renderLineCell(
                                              "medicalAid",
                                              medOut
                                            )}
                                            <td className="acc-list-num small">
                                              {shiftToPatient > 0 ? (
                                                <span className="fst-italic text-muted">
                                                  R{" "}
                                                  {formatCurrency(
                                                    shiftToPatient
                                                  )}{" "}
                                                  → Patient
                                                </span>
                                              ) : (
                                                ""
                                              )}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </React.Fragment>
                                  );
                                })}
                              {/* Custom allocation row */}
                              <tr>
                                <td />
                                <td>
                                  {new Date().toLocaleDateString()}
                                </td>
                                <td colSpan={2} className="fst-italic">
                                  Allocate an amount to the account
                                </td>
                                <td
                                  className={`text-end ${
                                    customAllocSource === "patient"
                                      ? "bg-primary text-white"
                                      : ""
                                  }`}
                                >
                                  <input
                                    className="text-end"
                                    name="customAllocPatient"
                                    type="text"
                                    value={customAllocPatient}
                                    onChange={handleChange}
                                    onBlur={() =>
                                      handleCustomAllocBlur("patient")
                                    }
                                    disabled={
                                      allSelectedInvoiceIds.length > 0 ||
                                      customAllocSource === "medicalAid"
                                    }
                                    placeholder="R 0.00"
                                    style={{
                                      border: "none",
                                      outline: "none",
                                      background: "transparent",
                                      width: "100%",
                                      fontSize: "inherit",
                                      color: customAllocSource === "patient" ? "white" : "inherit",
                                    }}
                                  />
                                </td>
                                <td
                                  className={`text-end ${
                                    customAllocSource === "medicalAid"
                                      ? "bg-primary text-white"
                                      : ""
                                  }`}
                                >
                                  <input
                                    className="text-end"
                                    name="customAllocMedAid"
                                    type="text"
                                    value={customAllocMedAid}
                                    onChange={handleChange}
                                    onBlur={() =>
                                      handleCustomAllocBlur("medicalAid")
                                    }
                                    disabled={
                                      allSelectedInvoiceIds.length > 0 ||
                                      customAllocSource === "patient"
                                    }
                                    placeholder="R 0.00"
                                    style={{
                                      border: "none",
                                      outline: "none",
                                      background: "transparent",
                                      width: "100%",
                                      fontSize: "inherit",
                                      color: customAllocSource === "medicalAid" ? "white" : "inherit",
                                    }}
                                  />
                                </td>
                                <td className="text-end">
                                  {(() => {
                                    const parse = (v) =>
                                      parseFloat(
                                        String(v || "").replace(
                                          /[^0-9.]/g,
                                          ""
                                        )
                                      ) || 0;
                                    const total =
                                      parse(customAllocPatient) +
                                      parse(customAllocMedAid);
                                    return total > 0
                                      ? `R ${formatCurrency(total)}`
                                      : "";
                                  })()}
                                </td>
                              </tr>
                            </tbody>
                          </Table>
                        </div>

                        {/* Mobile: card per invoice with expandable lines */}
                        <div className="d-lg-none mt-2">
                          {invoiceList
                            .filter(
                              (inv) =>
                                inv.practice?.practiceNumber ===
                                  selectedPractice &&
                                !["Paid", "Pending"].includes(inv.status)
                            )
                            .map((inv) => {
                              const balance =
                                outstandingBalances[inv._id] || {};
                              const patientAmount =
                                balance.patientOutstanding || 0;
                              const medAidAmount =
                                balance.medAidOutstanding || 0;
                              const totalOutstanding =
                                balance.totalOutstanding || 0;
                              const isExpanded = expandedInvoices.has(inv._id);
                              const lines = inv.lines || [];
                              const perLineBlocked =
                                lines.length > 0 && !canPayPerLine(inv);

                              return (
                                <div className="acc-card" key={`m-${inv._id}`}>
                                  <div className="acc-card-top">
                                    <span className="acc-card-id">
                                      {inv.invoiceNumber}
                                    </span>
                                    <span className="acc-card-amount">
                                      R {formatCurrency(totalOutstanding)}
                                    </span>
                                  </div>
                                  <div className="acc-card-body">
                                    <div className="acc-card-field">
                                      <span className="acc-card-label">
                                        Date
                                      </span>
                                      <span className="acc-card-value">
                                        {new Date(
                                          inv.invoiceDate
                                        ).toLocaleDateString()}
                                      </span>
                                    </div>
                                    <div className="acc-card-field">
                                      <span className="acc-card-label">
                                        Status
                                      </span>
                                      <span className="acc-card-value">
                                        <Badge bg="secondary">
                                          {inv.status}
                                        </Badge>
                                      </span>
                                    </div>
                                    <div className="d-flex gap-2 mt-1">
                                      <Button
                                        size="sm"
                                        className="flex-fill"
                                        variant={
                                          isCellSelected(inv._id, "patient")
                                            ? "primary"
                                            : "outline-primary"
                                        }
                                        disabled={
                                          patientAmount <= 0 ||
                                          !!customAllocSource ||
                                          (paidBy && paidBy !== "patient")
                                        }
                                        onClick={() =>
                                          handleCellClick(
                                            inv._id,
                                            "patient",
                                            patientAmount
                                          )
                                        }
                                      >
                                        Patient R{" "}
                                        {formatCurrency(patientAmount)}
                                      </Button>
                                      <Button
                                        size="sm"
                                        className="flex-fill"
                                        variant={
                                          isCellSelected(inv._id, "medicalAid")
                                            ? "primary"
                                            : "outline-primary"
                                        }
                                        disabled={
                                          medAidAmount <= 0 ||
                                          !!customAllocSource ||
                                          (paidBy && paidBy !== "medicalAid")
                                        }
                                        onClick={() =>
                                          handleCellClick(
                                            inv._id,
                                            "medicalAid",
                                            medAidAmount
                                          )
                                        }
                                      >
                                        Med Aid R{" "}
                                        {formatCurrency(medAidAmount)}
                                      </Button>
                                    </div>
                                  </div>
                                  {lines.length > 0 && !perLineBlocked && (
                                    <Button
                                      variant="link"
                                      className="p-0 mt-2 acc-card-expand-hint text-decoration-none"
                                      onClick={() =>
                                        toggleInvoiceExpand(inv._id)
                                      }
                                      aria-expanded={isExpanded}
                                    >
                                      <i
                                        className={`fa-solid me-1 ${
                                          isExpanded
                                            ? "fa-chevron-up"
                                            : "fa-chevron-down"
                                        }`}
                                      />
                                      {isExpanded
                                        ? "Hide lines"
                                        : `Pay specific lines (${lines.length})`}
                                    </Button>
                                  )}
                                  {perLineBlocked && (
                                    <div className="small text-muted mt-2">
                                      <i className="fa-solid fa-circle-info me-1" />
                                      {PER_LINE_BLOCKED_MSG}
                                    </div>
                                  )}
                                  {isExpanded &&
                                    lines.length > 0 &&
                                    !perLineBlocked && (
                                    <div className="acc-card-lines">
                                      {lines.map((line) => {
                                        const key = `${inv._id}_${line.lineNumber}`;
                                        const sel = selectedLineCells[key];
                                        const patOut = lineOutstanding(
                                          line,
                                          "patient"
                                        );
                                        const medOut = lineOutstanding(
                                          line,
                                          "medicalAid"
                                        );
                                        const selAmount = sel
                                          ? parseMoneyValue(sel.amountText)
                                          : 0;
                                        const shiftToPatient =
                                          sel &&
                                          sel.source === "medicalAid" &&
                                          selAmount < sel.maxAmount
                                            ? parseFloat(
                                                (
                                                  sel.maxAmount - selAmount
                                                ).toFixed(2)
                                              )
                                            : 0;
                                        return (
                                          <div
                                            className="acc-card-line"
                                            key={key}
                                          >
                                            <span className="acc-card-line-code">
                                              {line.tariffCode || "—"}
                                            </span>
                                            <span className="acc-card-line-rate">
                                              R{" "}
                                              {formatCurrency(patOut + medOut)}
                                            </span>
                                            {line.description && (
                                              <span className="acc-card-line-desc">
                                                {line.description}
                                              </span>
                                            )}
                                            <div
                                              className="d-flex gap-2 mt-1"
                                              style={{ gridColumn: "1 / -1" }}
                                            >
                                              <Button
                                                size="sm"
                                                className="flex-fill"
                                                variant={
                                                  sel?.source === "patient"
                                                    ? "primary"
                                                    : "outline-primary"
                                                }
                                                disabled={
                                                  patOut <= 0 ||
                                                  !!customAllocSource ||
                                                  (paidBy &&
                                                    paidBy !== "patient")
                                                }
                                                onClick={() =>
                                                  toggleLineCell(
                                                    inv._id,
                                                    line,
                                                    "patient"
                                                  )
                                                }
                                              >
                                                Patient R{" "}
                                                {formatCurrency(patOut)}
                                              </Button>
                                              <Button
                                                size="sm"
                                                className="flex-fill"
                                                variant={
                                                  sel?.source === "medicalAid"
                                                    ? "primary"
                                                    : "outline-primary"
                                                }
                                                disabled={
                                                  medOut <= 0 ||
                                                  !!customAllocSource ||
                                                  (paidBy &&
                                                    paidBy !== "medicalAid")
                                                }
                                                onClick={() =>
                                                  toggleLineCell(
                                                    inv._id,
                                                    line,
                                                    "medicalAid"
                                                  )
                                                }
                                              >
                                                Med Aid R{" "}
                                                {formatCurrency(medOut)}
                                              </Button>
                                            </div>
                                            {sel && (
                                              <div
                                                style={{
                                                  gridColumn: "1 / -1",
                                                }}
                                              >
                                                <InputGroup
                                                  size="sm"
                                                  className="mt-1"
                                                >
                                                  <InputGroup.Text>
                                                    Received
                                                  </InputGroup.Text>
                                                  <Form.Control
                                                    className="text-end"
                                                    value={sel.amountText}
                                                    onChange={(e) =>
                                                      handleLineAmountChange(
                                                        key,
                                                        e.target.value
                                                      )
                                                    }
                                                    onBlur={() =>
                                                      handleLineAmountBlur(key)
                                                    }
                                                  />
                                                </InputGroup>
                                                {shiftToPatient > 0 && (
                                                  <small className="fst-italic text-muted">
                                                    R{" "}
                                                    {formatCurrency(
                                                      shiftToPatient
                                                    )}{" "}
                                                    moves to the patient
                                                  </small>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}

                          {/* Mobile: custom allocation to unallocated funds */}
                          <div className="acc-card">
                            <div className="acc-card-top">
                              <span className="acc-card-id">
                                Allocate an amount to the account
                              </span>
                            </div>
                            <div className="acc-card-body">
                              <div className="acc-card-field">
                                <span className="acc-card-label">
                                  Patient
                                </span>
                                <Form.Control
                                  size="sm"
                                  className="text-end"
                                  name="customAllocPatient"
                                  type="text"
                                  value={customAllocPatient}
                                  onChange={handleChange}
                                  onBlur={() =>
                                    handleCustomAllocBlur("patient")
                                  }
                                  disabled={
                                    allSelectedInvoiceIds.length > 0 ||
                                    customAllocSource === "medicalAid"
                                  }
                                  placeholder="R 0.00"
                                  style={{ maxWidth: 140 }}
                                />
                              </div>
                              <div className="acc-card-field">
                                <span className="acc-card-label">
                                  Med Aid
                                </span>
                                <Form.Control
                                  size="sm"
                                  className="text-end"
                                  name="customAllocMedAid"
                                  type="text"
                                  value={customAllocMedAid}
                                  onChange={handleChange}
                                  onBlur={() =>
                                    handleCustomAllocBlur("medicalAid")
                                  }
                                  disabled={
                                    allSelectedInvoiceIds.length > 0 ||
                                    customAllocSource === "patient"
                                  }
                                  placeholder="R 0.00"
                                  style={{ maxWidth: 140 }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        </div>
                        <p className="text-muted">
                          You can only allocate payments against invoices that
                          are not in "Pending" or "Paid" status.
                        </p>
                      </>
                    )}
                  </Col>
                </Row>
              </>
            )}
          </Form>
        </Modal.Body>

        <Modal.Footer className="gap-2 justify-content-start">
          {paymentMethod === "paymentLink" && (
            <>
              <p className="fw-bold text-primary">Send link via:</p>
              {bankingDetails?.ozow?.paymentLink && (
                <Button
                  variant="outline-primary"
                  onClick={handleOzowLink}
                  disabled={loading}
                >
                  {loading ? (
                    <Spinner animation="border" size="sm" role="status" />
                  ) : (
                    <img src={ozow} alt="Ozow" height={20} />
                  )}
                </Button>
              )}
              {emailLinkSent && (
                <p className="mt-2 text-success">✔️ Payment link emailed</p>
              )}
              {bankingDetails?.yoco?.paymentLink && (
                <Button variant="outline-primary" onClick={handleAllocate} disabled={isAllocating}>
                  <img src={yoco} alt="Yoco" height={20} />
                </Button>
              )}
            </>
          )}

          {paymentMethod !== "paymentLink" && (
            <Col md={6}>
              <Button
                variant="primary"
                onClick={handleAllocate}
                style={{ minWidth: 140 }}
                disabled={isAllocating}
              >
                {isAllocating ? (
                  <Spinner animation="border" size="sm" role="status" />
                ) : (
                  <>
                    <i className="fa-solid fa-square-plus me-2"></i>
                    Allocate
                  </>
                )}
              </Button>
            </Col>
          )}
        </Modal.Footer>
      </Modal>

      {/* Post Receipt Modal */}
      <Modal
        show={showPostReceiptModal}
        onHide={() => setShowPostReceiptModal(false)}
        centered
      >
        <Modal.Header
          closeButton
          style={{ background: "#CFE2FF" }}
          className="rounded-0 text-primary d-flex align-items-center"
        >
          <Modal.Title as="h6" className="m-0">
            <i className="fa-light fa-circle-check me-2" />
            Receipt Created Successfully
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Your receipt was created successfully!</p>
          <p className="mb-2">Select what you would like to download or email:</p>
          <Form.Check
            type="checkbox"
            label="Receipt"
            checked={includeReceipt}
            onChange={(e) => setIncludeReceipt(e.target.checked)}
            className="mb-1"
          />
          <Form.Check
            type="checkbox"
            label="Statement (all transactions)"
            checked={includeStatement}
            onChange={(e) => setIncludeStatement(e.target.checked)}
            className="mb-3"
          />

          {showEmailFields && (
            <>
              <hr />
              <Form.Group className="mb-2">
                <Form.Label>Email</Form.Label>
                <InputGroup className="align-items-center">
                  <Form.Control
                    type="email"
                    value={receiptEmailPrimary}
                    onChange={(e) => setReceiptEmailPrimary(e.target.value)}
                    placeholder="Recipient email"
                  />
                  <Button
                    variant="link"
                    className="text-primary d-flex align-items-center"
                    onClick={addReceiptEmailField}
                    disabled={allReceiptExtrasUsed}
                    title={allReceiptExtrasUsed ? "Maximum emails added" : "Add another email"}
                  >
                    <FaPlus />
                  </Button>
                </InputGroup>
              </Form.Group>

              {EXTRA_EMAIL_FIELDS.map((field, idx) => {
                if (receiptExtraEmails[field] === undefined) return null;
                return (
                  <Form.Group key={field} className="mt-2">
                    <InputGroup className="align-items-center">
                      <Form.Control
                        type="email"
                        value={receiptExtraEmails[field]}
                        placeholder={`Additional Email ${idx + 2}`}
                        onChange={(e) => changeReceiptExtra(field, e.target.value)}
                      />
                      <Button
                        variant="link"
                        className="text-danger ms-0 d-flex align-items-center"
                        onClick={() => removeReceiptEmailField(field)}
                        title="Remove"
                      >
                        <FaTrash />
                      </Button>
                    </InputGroup>
                  </Form.Group>
                );
              })}

              <p className="text-muted mt-3 mb-0">
                Selected documents will be sent as PDF attachments to all email addresses entered above.
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={handleDownloadSelected}
            disabled={!includeReceipt && !includeStatement}
          >
            Download
          </Button>
          {!showEmailFields ? (
            <Button
              variant="outline-secondary"
              onClick={prepareEmailReceipt}
              disabled={!includeReceipt && !includeStatement}
            >
              Email
            </Button>
          ) : (
            <Button variant="outline-primary" onClick={handleSendEmail} disabled={emailSending || (!includeReceipt && !includeStatement)}>
              {emailSending ? (
                <Spinner animation="border" size="sm" role="status" />
              ) : (
                "Send Email"
              )}
            </Button>
          )}
          <Button variant="outline-secondary" onClick={() => { setShowEmailFields(false); navigate(0); }}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <AppToast
        show={toast.show}
        onClose={hideToast}
        variant={toast.variant}
        message={toast.message}
      />
    </>
  );
}
